// index.js
import {BitrixVue} from 'ui.vue3'
import {Loader} from './components/Loader'
import {VerificationManager} from './components/VerificationManager'

const API_METHODS = {
    CREATE_PRIVATE_CHAT: 'create.private.chat',
    RECENT_LIST_ALL: 'recent.list.all',
    MESSAGE_ADD: 'message.add',
    DIALOG_MESSAGES_GET: 'dialog.messages.get',
    DIALOG_READ: 'dialog.read',
    NOTIFY_READ: 'notify.read',
    NOTIFY_DELETE: 'notify.delete',
}

window.ChatManager = {
    instances: new Set(),

    register(instance) {
        this.instances.add(instance);
    },

    unregister(instance) {
        this.instances.delete(instance);
    },

    async refreshAll() {
        const promises = [];

        for (const instance of this.instances) {
            if (typeof instance.checkForUpdates === 'function') {
                promises.push(instance.checkForUpdates(true).catch(error => {
                    console.error('Error refreshing chat:', error);
                }));
            }
        }

        await Promise.all(promises);
    },

    async refreshGroupChat(groupId) {
        for (const instance of this.instances) {
            if (typeof instance.refreshGroupChat === 'function') {
                await instance.refreshGroupChat(groupId).catch(error => {
                    console.error('Error refreshing group chat:', error);
                });
            }
        }
    }
};

export class Chat {
    static COLOR_PALETTE = [
        '#FF9E44', '#51E2F5', '#FF70A6', '#FFDB58', '#5CEEFF',
        '#FF5E5B', '#A0FE72', '#FF5F91', '#FFD166', '#00CCBF',
        '#FF9A8B', '#7BFFE9', '#FFA447', '#D972FF', '#FFEA64',
        '#4FACF7', '#FF5C77', '#C5F277', '#FF8C61', '#8A7EF2',
        '#FFD271', '#00B4D8', '#FF6B6B', '#9DFF94', '#FF9CDA',
        '#FFCC33', '#42DDF5', '#FF7D42', '#7AE7FF', '#FF4F79',
        '#B0FC6F', '#FF8A8C', '#5CE1E6', '#FFB142', '#CE83FF',
        '#FFEAA5', '#4BC0C8', '#FF886F', '#A5FFFC', '#FF63A5',
        '#FF373A'
    ];
    #application = null
    #options = {
        showToggleButton: true,
        listenerInterval: 20000,
        fastCheckInterval: 3000,
        onUnreadUpdate: null,
        lastUnreadState: {total: 0, private: 0, group: 0, notifications: 0},
        isGroupChat: false,
        groupId: null
    }
    #listenerInterval = null
    #fastCheckInterval = null
    #isListening = false
    #isCheckingUpdates = false
    #hasRecentActivity = false
    #unreadState = {
        total: 0,
        private: 0,
        group: 0,
        notifications: 0,
        lastUpdate: 0,
    }
    #lastServerData = null
    #previousCounters = {private: 0, group: 0, notifications: 0}
    #isUpdatingSession = false
    #activeChatUpdateInterval = null
    #pageMessagesInstances = null
    #currentReply = null
    #syncInterval = null
    #apiCache = new Map()
    #pendingRequests = new Map()
    #isManualRefresh = false;
    #isInitialLoad = new Map();
    #readObservers = new Map();
    #unreadMessages = new Map();
    #lastReadTimestamps = new Map();
    #hasAutoOpenedGroupChat = false;
    #wasManuallyOpened = false;
    #lastMessageCheck = new Map();
    #lastMessageIds = new Map();
    #newMessagesNotifications = new Map();
    #intersectionObservers = new Map();
    #notifiedMessages = new Map();

    constructor(rootNode, options = {}) {
        this.rootNode = document.querySelector(rootNode)
        this.#options = {
            ...this.#options,
            ...options,
        }

        // Определяем режим полноэкранного отображения
        this.isFullscreen = this.rootNode?.classList.contains('im-chat-fullscreen') || options.isFullscreen || false

        this.#pageMessagesInstances = {};
        this.#currentReply = null;
        this.instanceId = Math.random().toString(36).substr(2, 9);

        if (typeof BX.Vue === 'undefined') {
            console.error('BX.Vue не доступен')
        }

        this.api = {
            openChat: (chatId, userId, initialMessage = null) => this.openChat(chatId,
                userId, initialMessage),
            refresh: () => this.refresh(),
        }

        if (window.ChatManager) {
            window.ChatManager.register(this);
        }

        this.#setupEventListeners();
        this.#setupCrossInstanceCommunication();

        document.addEventListener('chatVerificationComplete', (event) => {
            if (event.detail && event.detail.verified) {
                // Обновляем глобальные переменные
                window.userVerified = true;
                window.userId = event.detail.userId || window.userId;

                // Обновляем состояние чата
                if (this.#application) {
                    const app = this.#application._instance.proxy;
                    app.onVerificationComplete(event.detail);
                }

                // Перезапускаем прослушивание
                if (this.#isAuthenticated()) {
                    this.startListening();
                    this.checkForUpdates(true);
                }
            }
        });

        this.scrollPositions = new Map();
        this.unreadMessages = new Map();
        this.scrollHandlers = new Map();

        this.isUserScrolled = false;
        this.replyHandlers = new Map();
        this.cancelReply = this.cancelReply.bind(this);
        this.activateReply = this.activateReply.bind(this);

        this.#hasAutoOpenedGroupChat = false;
        this.#wasManuallyOpened = false;
        this.userScrollBehavior = new Map();
        this.scrollThreshold = 100;
        this.scrollHandlers = new Map();
        this.#setupIntersectionObserver();

        this.#setupReadObservers();
        this.#lastMessageCheck = new Map();
        this.#lastMessageIds = new Map();
        this.#notifiedMessages = new Map();
    }

    #getDeterministicColor(chatId, type = 'private') {
        if (!chatId) return '#007bff';

        // Простой и надежный хэш
        let hash = 0;
        const key = `${type}_${chatId}`;

        for (let i = 0; i < key.length; i++) {
            hash = ((hash << 5) - hash) + key.charCodeAt(i);
            hash = hash | 0; // Convert to 32bit integer
        }

        // Абсолютное значение и модуль
        const index = Math.abs(hash) % Chat.COLOR_PALETTE.length;
        return Chat.COLOR_PALETTE[index];
    }

    #setupReadObservers() {
        // Intersection Observer для отслеживания видимости сообщений
        this.readObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting && this.#isChatOpen()) {
                    const messageElement = entry.target;
                    const messageId = messageElement.dataset.messageId;
                    const chatId = messageElement.dataset.chatId;
                    const type = messageElement.dataset.chatType;

                    if (messageId && chatId && type) {
                        this.#markMessageAsRead(messageId, chatId, type);

                        // Помечаем сообщение как прочитанное в UI
                        messageElement.classList.remove('unread');
                        messageElement.classList.add('read');
                    }
                }
            });
        }, {
            threshold: 0.7, // 70% видимости для уверенности
            rootMargin: '0px 0px -50px 0px' // Игнорируем нижние 50px
        });
    }

    #setupIntersectionObserver() {
        // Observer для отслеживания видимости нижней части чата
        this.bottomObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    // Нижняя часть видна - скрываем уведомление
                    const chatId = entry.target.dataset.chatId;
                    const type = entry.target.dataset.chatType;
                    this.#hideNewMessagesNotification(type, chatId);
                }
            });
        }, {threshold: 0.1});
    }

    #markMessageAsRead(messageId, chatId, type) {
        const key = `${type}_${chatId}`;
        const now = Date.now();

        // Дебаунс запросов - отправляем не чаще чем раз в 2 секунды
        if (this.#lastReadTimestamps.has(key)) {
            const lastRead = this.#lastReadTimestamps.get(key);
            if (now - lastRead < 3000) {
                return;
            }
        }

        this.#lastReadTimestamps.set(key, now);

        this.#updateChatUnreadCount(chatId, type, 0);

        // Отправляем запрос на сервер
        this.callBitrixAPI(API_METHODS.DIALOG_READ, {
            [type === 'private' ? 'DIALOG_ID' : 'CHAT_ID']: chatId,
        }).then(response => {
            if (response.status === 'success') {
                this.#updateChatUnreadCount(chatId, type, 0);
            }
        }).catch(error => {
            this.#restoreChatUnreadCount(chatId, type);
        });
    }

    #updateChatUnreadCount(chatId, type, count) {
        if (!this.#application) return;

        const app = this.#application._instance.proxy;
        const chatList = type === 'private' ? app.privateChats : app.groupChats;

        const chatIndex = chatList.findIndex(chat => chat.id === chatId);
        if (chatIndex > -1) {
            // Сохраняем предыдущее значение для возможного отката
            const previousUnread = chatList[chatIndex].unread || 0;

            // Обновляем счетчик
            if (type === 'private') {
                app.privateChats[chatIndex].unread = count;
                // Сохраняем для отката
                app.privateChats[chatIndex]._previousUnread = previousUnread;
            } else {
                app.groupChats[chatIndex].unread = count;
                app.groupChats[chatIndex]._previousUnread = previousUnread;
            }

            // Обновляем общий счетчик
            this.#updateTotalUnreadCount();
        }
    }

    #restoreChatUnreadCount(chatId, type) {
        if (!this.#application) return;

        const app = this.#application._instance.proxy;
        const chatList = type === 'private' ? app.privateChats : app.groupChats;

        const chatIndex = chatList.findIndex(chat => chat.id === chatId);
        if (chatIndex > -1 && chatList[chatIndex]._previousUnread !== undefined) {
            if (type === 'private') {
                app.privateChats[chatIndex].unread = app.privateChats[chatIndex]._previousUnread;
            } else {
                app.groupChats[chatIndex].unread = app.groupChats[chatIndex]._previousUnread;
            }

            this.#updateTotalUnreadCount();
        }
    }

    #updateTotalUnreadCount() {
        if (!this.#application) return;

        const app = this.#application._instance.proxy;

        const privateUnread = app.privateChats.reduce((sum, chat) => sum + (chat.unread || 0), 0);
        const groupUnread = app.groupChats.reduce((sum, chat) => sum + (chat.unread || 0), 0);
        const notificationsUnread = app.notifications.filter(n => !n.read).length;

        const totalUnread = privateUnread + groupUnread + notificationsUnread;

        // Обновляем состояние
        this.#unreadState = {
            total: totalUnread,
            private: privateUnread,
            group: groupUnread,
            notifications: notificationsUnread,
            lastUpdate: Date.now()
        };

        // Вызываем callback если есть
        this.#options.onUnreadUpdate?.(totalUnread, this.#unreadState);
    }

    #shouldShowNotification(type, chatId) {
        if (!this.#application) return false;

        const app = this.#application._instance.proxy;
        const messages = type === 'private' ? app.privateMessages : app.groupMessages;

        if (messages.length === 0) return false;

        // Получаем последнее сообщение
        const lastMessage = messages[messages.length - 1];

        // Проверяем, новое ли это сообщение (последние 2 минуты)
        const messageTime = new Date(lastMessage.date).getTime();
        const currentTime = Date.now();
        const isRecent = (currentTime - messageTime) < 120000; // 2 минуты

        // Проверяем, не наше ли это сообщение
        const isOutgoing = lastMessage.outgoing;

        // Проверяем, не было ли уже показано уведомление для этого сообщения
        const key = `${type}_${chatId}_${lastMessage.id}`;
        const alreadyNotified = this.#notifiedMessages.has(key);

        if (isRecent && !isOutgoing && !alreadyNotified) {
            this.#notifiedMessages.set(key, true);
            return true;
        }

        return false;
    }

    #setupScrollTracking() {
        if (!this.#application) return;

        const app = this.#application._instance.proxy;

        // Отслеживаем скролл для приватных сообщений
        if (app.$refs.privateMessages) {
            this.#setupContainerScrollTracking('private', app.$refs.privateMessages);
        }

        // Отслеживаем скролл для групповых сообщений
        if (app.$refs.groupMessages) {
            this.#setupContainerScrollTracking('group', app.$refs.groupMessages);
        }
    }

    #setupContainerScrollTracking(type, container) {
        if (!container || this.scrollHandlers.has(container)) return;

        const handler = () => {
            const isNearBottom = this.#isNearBottom(container);
            const chatId = type === 'private' ?
                this.#application._instance.proxy.activePrivateChat :
                this.#application._instance.proxy.activeGroupChat;

            if (chatId) {
                const key = `${type}_${chatId}`;
                this.userScrollBehavior.set(key, {
                    isNearBottom,
                    lastScrollTime: Date.now(),
                    manualScroll: !isNearBottom // Если не у низа - значит пользователь прокрутил вручную
                });
            }
        };

        container.addEventListener('scroll', handler);
        this.scrollHandlers.set(container, handler);
    }

    #shouldAutoScrollToBottom(chatId, type) {
        const key = `${type}_${chatId}`;
        const behavior = this.userScrollBehavior.get(key);

        // Если пользователь недавно прокрутил вручную - не автоскроллим
        if (behavior && behavior.manualScroll) {
            // Проверяем, не было ли слишком долгого простоя
            const timeSinceLastScroll = Date.now() - behavior.lastScrollTime;
            return timeSinceLastScroll > 10000; // 10 секунд бездействия
        }

        // По умолчанию скроллим если открыли чат впервые
        return !behavior || behavior.isNearBottom;
    }

    #setupCrossInstanceCommunication() {
        document.addEventListener('chatMessageSent', (event) => {
            this.#handleExternalMessage(event.detail);
        });

        document.addEventListener('chatNeedsRefresh', () => {
            this.checkForUpdates(true).catch(console.error);
        });

        window.addEventListener('storage', (e) => {
            if (e.key === 'chatLastUpdate') {
                this.checkForUpdates(true).catch(console.error);
            }
        });
    }

    #handleExternalMessage(messageData) {
        if (!this.#application) return;

        const app = this.#application._instance.proxy;
        const {chatId, message, type} = messageData;

        if (this.#options.isGroupChat && type === 'group' &&
            this.#options.groupId && chatId != this.#options.groupId) {
            return;
        }

        // Добавляем обработку родительских сообщений
        const formattedMessage = {
            ...message,
            time: app.formatTime(message.date),
            outgoing: parseInt(message.senderId) === parseInt(app.userId),
            isSystem: message.system === 1,
            name: message.name || '',
            parentId: message.parentId || message.parentMessageId,
            parentText: message.parentMessageText || message.parentText,
            parentAuthor: message.parentMessageAuthor || message.parentAuthor
        };

        const isActiveChat = (type === 'private' && app.activePrivateChat === chatId) ||
            (type === 'group' && app.activeGroupChat === chatId);

        if (isActiveChat) {
            // Для активного чата добавляем сообщение в UI
            this.#addMessageToUI(formattedMessage, type);

            // ПОКАЗЫВАЕМ УВЕДОМЛЕНИЕ о новом сообщении
            setTimeout(() => {
                const container = app.$refs[`${type}Messages`];
                if (container) {
                    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight <= 100;
                    if (!isNearBottom && this.#shouldShowNotification(type, chatId)) {
                        app.showNewMessagesNotification(type, chatId, 1);
                    }

                    // Настраиваем observer для нового сообщения
                    const newMessageElement = container.querySelector(`[data-message-id="${message.id}"]`);
                    if (newMessageElement) {
                        newMessageElement.dataset.chatId = chatId;
                        newMessageElement.dataset.chatType = type;
                        this.readObserver.observe(newMessageElement);
                    }
                }
            }, 100);
        }

        // Всегда обновляем список чатов
        this.#updateChatList(messageData);
    }

    #showNotificationForExternalMessage(messageData) {
        if (!this.#application) return;

        const app = this.#application._instance.proxy;
        const {chatId, message, type} = messageData;

        // Проверяем, активен ли этот чат в данный момент
        const isActiveChat = (type === 'private' && app.activePrivateChat === chatId) ||
            (type === 'group' && app.activeGroupChat === chatId);

        if (isActiveChat) {
            // Для активного чата используем умную прокрутку
            this.#smartScrollOnNewMessage(type);
        } else {
            // Для неактивного чата обновляем список и показываем уведомление в UI
            this.#updateChatList(messageData);

            // Можно добавить звуковое уведомление или badge
            this.#showDesktopNotification(message, type);
        }
    }

    #showDesktopNotification(message, type) {
        if ('Notification' in window && Notification.permission === 'granted') {
            new Notification('Новое сообщение', {
                body: message.text.substring(0, 100) + '...',
                icon: '/favicon.ico'
            });
        }
    }

    #addMessageToUI(message, type) {
        if (!this.#application) return;

        const app = this.#application._instance.proxy;
        const messages = type === 'private' ? app.privateMessages : app.groupMessages;

        // Проверяем, нет ли уже такого сообщения
        const messageExists = messages.some(m => m.id === message.id);
        if (!messageExists) {
            const formattedMessage = {
                ...message,
                time: app.formatTime(message.date),
                outgoing: parseInt(message.senderId) === parseInt(app.userId),
                isSystem: message.system === 1,
                name: message.name || '',
                parentId: message.parentId || message.parentMessageId,
                parentText: message.parentMessageText,
                parentAuthor: message.parentMessageAuthor
            };

            if (type === 'private') {
                app.privateMessages = [...messages, formattedMessage];
            } else {
                app.groupMessages = [...messages, formattedMessage];
            }

            this.$nextTick(() => {
                const container = app.$refs[`${type}Messages`];
                if (container) {
                    const isNearBottom = this.#isNearBottom(container);
                    if (!isNearBottom) {
                        // Пользователь прокрутил вверх - показываем уведомление
                        const chatId = type === 'private' ? app.activePrivateChat : app.activeGroupChat;
                        this.#showNewMessagesNotification(type, chatId, 1);
                    }
                }
            });

            this.#smartScrollOnNewMessage(type);
        }
    }

    #smartScrollOnNewMessage(type) {
        if (!this.#application) return;

        const app = this.#application._instance.proxy;
        const container = app.$refs[`${type}Messages`];
        if (!container) return;

        // Проверяем, находится ли пользователь внизу
        const isNearBottom = this.#isNearBottom(container);

        // Скроллим только если пользователь близко к низу
        if (isNearBottom) {
            this.scrollToBottom(`${type}Messages`);
        }
    }


    #updateChatList(messageData) {
        if (!this.#application) return;

        const app = this.#application._instance.proxy;
        const {chatId, message, type} = messageData;

        const chatList = type === 'private' ? app.privateChats : app.groupChats;
        const chatIndex = chatList.findIndex(chat => chat.id === chatId);

        if (chatIndex > -1) {
            const updatedChat = {
                ...chatList[chatIndex],
                lastMessage: message.text,
                date: new Date().toISOString(),
                unread: type === 'private' ? (chatList[chatIndex].unread || 0) + 1 : 0
            };

            if (type === 'private') {
                app.privateChats = [
                    updatedChat,
                    ...app.privateChats.filter(chat => chat.id !== chatId)
                ];
            } else {
                app.groupChats = [
                    updatedChat,
                    ...app.groupChats.filter(chat => chat.id !== chatId)
                ];
            }
        }
    }

    #notifyOtherInstances(messageData) {
        const event = new CustomEvent('chatMessageSent', {
            detail: messageData
        });
        document.dispatchEvent(event);

        localStorage.setItem('chatLastUpdate', Date.now());
    }

    #notifyGroupChats(messageData) {
        // Специальное событие для групповых чатов
        const groupEvent = new CustomEvent('groupChatMessage', {
            detail: messageData
        });
        document.dispatchEvent(groupEvent);

        // Также отправляем обычное событие для обратной совместимости
        this.#notifyOtherInstances(messageData);
    }

    #setupEventListeners() {
        document.addEventListener('chatNeedsRefresh', () => {
            if (this.#isAuthenticated()) {
                this.checkForUpdates(true).catch(error => {
                    console.error('Event refresh error:', error);
                });
            }
        });
    }

    getApplication() {
        return this.#application
    }

    #callUnreadCallback() {
        if (!this.#application || typeof this.#options.onUnreadUpdate !==
            'function') return

        const app = this.#application._instance.proxy
        const newState = {
            total: app.totalUnread,
            private: app.privateChats.reduce((sum, c) => sum + (c.unread || 0), 0),
            group: app.groupChats.reduce((sum, c) => sum + (c.unread || 0), 0),
            notifications: app.notifications.filter(n => !n.read).length,
        }

        if (JSON.stringify(newState) !== JSON.stringify(this.#unreadState)) {
            this.#options.onUnreadUpdate(newState.total, newState)
            this.#unreadState = newState
        }
    }

    #preserveTempChats(currentChats, newChats) {
        const tempChats = currentChats.filter(chat => chat.isTemp)
        return [
            ...newChats,
            ...tempChats.filter(tempChat =>
                !newChats.some(newChat => newChat.id === tempChat.id),
            ),
        ]
    }

    startListening() {
        if (this.#isListening) return

        this.#isListening = true

        this.#listenerInterval = setInterval(() => {
            this.checkForUpdates()
        }, this.#options.listenerInterval)

        this.#fastCheckInterval = setInterval(() => {
            if (this.#hasRecentActivity) {
                this.checkForUpdates()
                this.#hasRecentActivity = false
            }
        }, this.#options.fastCheckInterval)

        this.#syncInterval = setInterval(() => {
            if (this.#isAuthenticated() && window.ChatManager) {
                window.ChatManager.refreshAll().catch(error => {
                    console.error('Sync error:', error);
                });
            }
        }, this.#options.listenerInterval);

        setTimeout(() => this.checkForUpdates(), 1000)
    }

    stopListening() {
        if (!this.#isListening) return

        this.#isListening = false
        clearInterval(this.#listenerInterval)
        clearInterval(this.#fastCheckInterval)
        this.#listenerInterval = null
        this.#fastCheckInterval = null
        this.#stopActiveChatUpdates()

        if (this.#syncInterval) {
            clearInterval(this.#syncInterval);
            this.#syncInterval = null;
        }
    }

    #startActiveChatUpdates() {
        this.#stopActiveChatUpdates()

        if (this.#isChatOpen()) {
            this.#activeChatUpdateInterval = setInterval(() => {
                if (this.#isChatOpen()) { // Проверяем на каждой итерации
                    this.#updateActiveChat();
                }
            }, 5000);
        }
    }

    #stopActiveChatUpdates() {
        if (this.#activeChatUpdateInterval) {
            clearInterval(this.#activeChatUpdateInterval)
            this.#activeChatUpdateInterval = null
        }
    }

    #updateActiveChat() {
        if (!this.#application || !this.#isChatOpen()) return;

        const app = this.#application._instance.proxy
        const activeChatId = app.activePrivateChat || app.activeGroupChat
        const chatType = app.activePrivateChat ? 'private' : 'group'

        if (activeChatId) {
            this.#loadMessagesForChat(activeChatId, chatType)
        }
    }

    #isChatOpen() {
        if (!this.#application) return false;

        if (this.isFullscreen) {
            return true;
        }

        return this.#application._instance.proxy.isOpen;
    }

    #cleanupTempChats(currentChats, newChats) {
        return currentChats.filter(chat =>
            !chat.isTemp ||
            !newChats.some(newChat => newChat.id === chat.id),
        )
    }

    getUnreadCounters() {
        return {...this.#unreadState}
    }

    async checkForUpdates(force = false) {
        if (!this.#isAuthenticated()) {
            return;
        }

        if (this.#isCheckingUpdates && !force) {
            return;
        }

        this.#isCheckingUpdates = true;

        try {
            const response = await this.callBitrixAPI(API_METHODS.RECENT_LIST_ALL, {}, {
                cacheTtl: force ? 0 : 5000
            });

            if (response.status !== 'success' || !response.data) {
                console.error('Ошибка получения обновлений:', response);
                return;
            }

            const data = response.data;
            const newCounters = {
                private: data.totalUnreadChats || 0,
                group: data.totalUnreadGroup || 0,
                notifications: data.totalUnreadNotice || 0,
            };
            const totalUnread = newCounters.private + newCounters.group + newCounters.notifications;

            this.#updateCountersImmediately(totalUnread, newCounters);

            if (this.#application) {
                const app = this.#application._instance.proxy;
                const activeType = app.activeTab;
                const activeChatId = activeType === 'private' ? app.activePrivateChat : app.activeGroupChat;

                if (activeChatId) {
                    setTimeout(() => {
                        const container = app.$refs[`${activeType}Messages`];
                        if (container) {
                            const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight <= 100;
                            const hasNewMessages = this.#hasNewMessagesSinceLastCheck(activeType, activeChatId);

                            if (!isNearBottom && hasNewMessages) {
                                app.showNewMessagesNotification(activeType, activeChatId, 1);
                            }

                            this.#setupMessageObservers(activeType, activeChatId);
                        }
                    }, 100);
                }

                const newPrivateChats = data.chats?.map(chat => ({
                    ...chat,
                    time: app.formatTime(chat.date),
                    unread: chat.unread || 0,
                })) || [];

                app.privateChats = this.#cleanupTempChats(
                    this.#preserveTempChats(app.privateChats, newPrivateChats),
                    newPrivateChats,
                );

                const newGroupChats = data.groupChats?.map(chat => ({
                    ...chat,
                    time: app.formatTime(chat.date),
                    unread: chat.unread || 0,
                })) || [];

                app.groupChats = newGroupChats;

                if (newGroupChats.length === 1 &&
                    !this.#hasAutoOpenedGroupChat &&
                    app.isOpen &&
                    app.activeTab === 'group' &&
                    !app.activeGroupChat) {

                    const singleGroupChat = newGroupChats[0];
                    this.#hasAutoOpenedGroupChat = true;
                    this.openGroupChat(singleGroupChat.id);
                } else if (app.activeGroupChat) {
                    // Если уже открыт чат, обновляем его сообщения
                    await this.#loadMessagesForChat(app.activeGroupChat, 'group');
                }

                app.notifications = data.notifications?.map(notif => ({
                    ...notif,
                    time: app.formatTime(notif.date),
                    read: notif.read || notif.unread === 0,
                })) || [];

                if (app.activeGroupChat) {
                    await this.#loadMessagesForChat(app.activeGroupChat, 'group', false);
                }
                if (app.activePrivateChat) {
                    await this.#loadMessagesForChat(app.activePrivateChat, 'private', false);
                }

                const chatUpdateEvent = new CustomEvent('chatUpdated', {
                    detail: {
                        type: 'group',
                        chatId: app.activeGroupChat,
                        messages: app.groupMessages
                    }
                });
                document.dispatchEvent(chatUpdateEvent);
            }
        } catch (error) {
            console.error('Ошибка при проверке обновлений:', error);
        } finally {
            this.#isCheckingUpdates = false;
        }
    }

    #hasNewMessagesSinceLastCheck(type, chatId) {
        if (!this.#application) return false;

        const app = this.#application._instance.proxy;
        const messages = type === 'private' ? app.privateMessages : app.groupMessages;

        if (messages.length === 0) return false;

        // Получаем последнее сообщение
        const lastMessage = messages[messages.length - 1];
        const key = `${type}_${chatId}`;

        // Проверяем, было ли это сообщение уже показано
        const lastKnownId = this.#lastMessageIds.get(key);

        if (lastKnownId !== lastMessage.id) {
            // Новое сообщение - сохраняем ID и возвращаем true
            this.#lastMessageIds.set(key, lastMessage.id);
            return true;
        }

        return false;
    }


    #isAuthenticated() {
        // Проверяем стандартную авторизацию
        // Проверяем стандартную авторизацию
        const standardUserId = this.#options.userId || BX.message('USER_ID');
        const standardAuth = !!standardUserId && parseInt(standardUserId) > 0;

        // Проверяем верификацию через VerificationManager
        const verificationAuth = window.userVerified === true &&
            window.userId &&
            parseInt(window.userId) > 0;

        // Если есть верификация, обновляем userId в опциях
        if (verificationAuth && !standardAuth) {
            this.#options.userId = window.userId;

            // Обновляем состояние в Vue-приложении если оно существует
            if (this.#application) {
                const app = this.#application._instance.proxy;
                app.isVerificationComplete = true;
                app.verificationUserId = window.userId;
                app.userId = window.userId;
                app.showVerification = false;
            }
        }

        return standardAuth || verificationAuth;
    }

    #updateUserAfterVerification() {
        if (window.userVerified && window.userId) {
            // Обновляем опции чата
            this.#options.userId = window.userId;

            // Если приложение уже создано, обновляем его
            if (this.#application) {
                const app = this.#application._instance.proxy;
                app.userId = window.userId;
                app.isVerificationComplete = true;
                app.verificationUserId = window.userId;

                // Перезапускаем прослушивание
                this.stopListening();
                this.startListening();

                // Обновляем данные
                app.loadData();
            }
        }
    }

    #updateCountersImmediately(total, breakdown) {
        const newState = {
            total,
            private: breakdown.private,
            group: breakdown.group,
            notifications: breakdown.notifications,
            lastUpdate: Date.now(),
        }

        const countersChanged =
            this.#previousCounters.private !== breakdown.private ||
            this.#previousCounters.group !== breakdown.group ||
            this.#previousCounters.notifications !== breakdown.notifications

        this.#previousCounters = {
            private: breakdown.private,
            group: breakdown.group,
            notifications: breakdown.notifications,
        }

        this.#unreadState = newState
        this.#options.onUnreadUpdate?.(total, newState, countersChanged)
    }

    async #loadMessagesForChat(chatId, type, isManual = false) {
        if (!this.#application || !this.#isChatOpen()) return;

        this.#isManualRefresh = isManual;

        const app = this.#application._instance.proxy;
        const method = API_METHODS.DIALOG_MESSAGES_GET;
        const paramName = type === 'private' ? 'DIALOG_ID' : 'CHAT_ID';

        const key = `${type}_${chatId}`;
        const isInitialLoad = !this.#isInitialLoad.has(key);

        try {
            const response = await this.callBitrixAPI(method, {
                [paramName]: chatId,
                // Добавляем параметр для получения информации о родительских сообщениях
                WITH_PARENT_MESSAGES: 'Y'
            }, {cacheTtl: 0});

            if (response.status === 'success') {
                const messages = (response.data || []).map(msg => ({
                    ...msg,
                    time: app.formatTime(msg.date),
                    outgoing: parseInt(msg.senderId) === parseInt(app.userId),
                    isSystem: msg.system === 1,
                    read: msg.read || parseInt(msg.senderId) === parseInt(app.userId),
                    parentId: msg.parentId || msg.parentMessageId,
                    parentText: msg.parent?.text || msg.parentText || msg.parentMessageText,
                    parentAuthor: msg.parent?.author || msg.parentAuthor || msg.parentMessageAuthor
                }));

                if (type === 'private') {
                    app.privateMessages = messages;
                } else {
                    app.groupMessages = messages;
                }

                this.#isInitialLoad.set(key, true);

                // Прокручиваем вниз ТОЛЬКО при первоначальной загрузке
                if (isInitialLoad) {
                    setTimeout(() => {
                        this.scrollToBottom(`${type}Messages`);
                    }, 100);
                }

                setTimeout(() => {
                    this.#setupMessageObservers(type, chatId);
                }, 100);


                // await this.callBitrixAPI(API_METHODS.DIALOG_READ, {
                //     [paramName]: chatId,
                // });

                setTimeout(() => {
                    this.#setupMessageObservers(type, chatId);
                }, 100);
            }

            this.#isManualRefresh = false;
        } catch (error) {
            this.#isManualRefresh = false;
            console.error(`Error loading ${type} messages:`, error);
        }
    }

    #setupMessageObservers(type, chatId) {
        if (!this.#application) return;

        const app = this.#application._instance.proxy;
        const container = app.$refs[`${type}Messages`];
        if (!container) return;

        // Останавливаем предыдущий observer для этого чата
        const key = `${type}_${chatId}`;
        if (this.#readObservers.has(key)) {
            this.#readObservers.get(key).disconnect();
        }

        // Находим все непрочитанные сообщения
        setTimeout(() => {
            // Находим все непрочитанные сообщения
            const unreadMessages = container.querySelectorAll('.message-wrapper.unread');

            unreadMessages.forEach(messageElement => {
                const messageId = messageElement.dataset.messageId;
                if (messageId) {
                    messageElement.dataset.chatId = chatId;
                    messageElement.dataset.chatType = type;
                    this.readObserver.observe(messageElement);
                }
            });
        }, 300);
    }

    async markVisibleMessagesAsRead() {
        if (!this.#application || !this.#isChatOpen()) return;

        const app = this.#application._instance.proxy;
        const activeType = app.activeTab;
        const activeChatId = activeType === 'private' ? app.activePrivateChat : app.activeGroupChat;

        if (!activeChatId) return;

        const container = app.$refs[`${activeType}Messages`];
        if (!container) return;

        // Находим видимые непрочитанные сообщения
        const visibleUnread = Array.from(container.querySelectorAll('.message-wrapper:not(.read)'))
            .filter(el => {
                const rect = el.getBoundingClientRect();
                return rect.top >= 0 && rect.bottom <= window.innerHeight;
            });

        if (visibleUnread.length > 0) {

            this.#updateChatUnreadCount(activeChatId, activeType, 0);

            visibleUnread.forEach(el => {
                el.classList.remove('unread');
                el.classList.add('read');
            });

            await this.callBitrixAPI(API_METHODS.DIALOG_READ, {
                [activeType === 'private' ? 'DIALOG_ID' : 'CHAT_ID']: activeChatId,
            }).then(response => {
                if (response.status === 'success') {
                    // Обновляем статус всех сообщений в этом чате
                    // this.#markAllMessagesAsRead(activeChatId, activeType);
                }
            }).catch(error => {
                console.error('Error marking chat as read:', error);
                // В случае ошибки восстанавливаем
                this.#restoreChatUnreadCount(activeChatId, activeType);
            });
        }
    }

    #smartScrollAfterLoad(chatId, type) {
        if (!this.#application) return;

        const app = this.#application._instance.proxy;
        const container = app.$refs[`${type}Messages`];
        if (!container) return;

        if (app.isUserScrolledUp) {
            // Пользователь прокрутил вверх - НЕ скроллим, сохраняем позицию
            return;
        }

        // Проверяем, находится ли пользователь внизу (или близко к низу)
        const isNearBottom = this.#isNearBottom(container);

        if (isNearBottom) {
            // Если пользователь внизу - скроллим к последнему сообщению
            this.scrollToBottom(`${type}Messages`);
        }
        // Если пользователь прокрутил вверх - НЕ скроллим, сохраняем позицию
    }

    #isNearBottom(container) {
        if (!container) return true;
        const threshold = 100; // 100px от нижнего края
        return container.scrollHeight - container.scrollTop - container.clientHeight <= threshold;
    }

    #autoScrollToRelevantMessage(chatId, type, isManual = false) {
        if (!this.#application) return;

        const app = this.#application._instance.proxy;
        const container = app.$refs[`${type}Messages`];
        if (!container) return;

        // Проверяем, нужно ли автоскроллить
        const shouldAutoScroll = isManual || this.#shouldAutoScrollToBottom(chatId, type);

        if (shouldAutoScroll) {
            const currentChat = type === 'private'
                ? app.privateChats.find(chat => chat.id === chatId)
                : app.groupChats.find(chat => chat.id === chatId);

            const hasUnread = currentChat && currentChat.unread > 0;

            if (hasUnread) {
                this.#scrollToFirstUnread(chatId, type);
            } else {
                this.scrollToBottom(`${type}Messages`);
            }
        } else {
            // Показываем уведомление о новых сообщениях
            this.#showNewMessagesNotification(type, chatId, 1);
        }
    }

    #scrollToFirstUnread(chatId, type) {
        if (!this.#application) return;

        if (this._scrollTimeout) {
            clearTimeout(this._scrollTimeout);
        }

        this._scrollTimeout = setTimeout(() => {
            const app = this.#application._instance.proxy;
            const container = app.$refs[`${type}Messages`];
            if (!container) return;

            // Ждем обновления DOM
            this.$nextTick(() => {
                // Ищем первое непрочитанное сообщение (можно добавить data-атрибут для идентификации)
                const messages = container.querySelectorAll('.message-wrapper:not(.system)');
                let firstUnreadElement = null;

                // Простой алгоритм: предполагаем, что последние N сообщений - непрочитанные
                const currentChat = type === 'private'
                    ? app.privateChats.find(chat => chat.id === chatId)
                    : app.groupChats.find(chat => chat.id === chatId);

                if (currentChat && currentChat.unread > 0 && messages.length > 0) {
                    const unreadCount = Math.min(currentChat.unread, messages.length);
                    const firstUnreadIndex = Math.max(0, messages.length - unreadCount);
                    firstUnreadElement = messages[firstUnreadIndex];
                }

                if (firstUnreadElement) {
                    // Прокручиваем к первому непрочитанному сообщению
                    firstUnreadElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    });

                    // Добавляем визуальный индикатор
                    this.#highlightUnreadMessage(firstUnreadElement);
                } else {
                    // Если не нашли непрочитанные, прокручиваем вниз
                    this.scrollToBottom(`${type}Messages`);
                }
            }, 300);
        });
    }

    #highlightUnreadMessage(messageElement) {
        // Добавляем временный класс для подсветки
        messageElement.classList.add('unread-highlight');

        // Убираем подсветку через 3 секунды
        setTimeout(() => {
            messageElement.classList.remove('unread-highlight');
        }, 3000);
    }

    #saveScrollPosition(chatId, type) {
        if (!this.#application) return;

        const app = this.#application._instance.proxy;
        const container = app.$refs[`${type}Messages`];

        if (container) {
            this.scrollPositions.set(`${type}_${chatId}`, {
                position: container.scrollTop,
                isAtBottom: this.#isAtBottom(container)
            });
        }
    }

    #showNewMessagesNotification(type, chatId, count) {
        if (!this.#application) return;

        const app = this.#application._instance.proxy;
        const container = app.$refs[`${type}Messages`];

        if (!container) return;

        // Удаляем старое уведомление если есть
        this.#hideNewMessagesNotification(type, chatId);

        const notification = document.createElement('div');
        notification.className = 'new-messages-notification';
        notification.innerHTML = `
            <div class="notification-content">
                <span>${count} нов${count === 1 ? 'ое' : 'ых'} сообщени${count === 1 ? 'е' : 'я'}</span>
                <button class="scroll-to-bottom-btn">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M16 13l-4 4-4-4M12 6v11"/>
                    </svg>
                </button>
            </div>
        `;

        container.appendChild(notification);
        this.#newMessagesNotifications.set(`${type}_${chatId}`, notification);

        // Обработчик клика на кнопку прокрутки
        notification.querySelector('.scroll-to-bottom-btn').addEventListener('click', () => {
            this.scrollToBottom(`${type}Messages`);
            this.#hideNewMessagesNotification(type, chatId);
        });

        // Автоскрытие через 10 секунд
        setTimeout(() => {
            this.#hideNewMessagesNotification(type, chatId);
        }, 10000);
    }

    #hideNewMessagesNotification(type, chatId) {
        const key = `${type}_${chatId}`;
        const notification = this.#newMessagesNotifications.get(key);
        if (notification && notification.parentNode) {
            notification.remove();
        }
        this.#newMessagesNotifications.delete(key);
    }

    #setupBottomIndicator(container, type, chatId) {
        if (!container) return;

        // Создаем индикатор нижней части
        const bottomIndicator = document.createElement('div');
        bottomIndicator.className = 'bottom-indicator';
        bottomIndicator.dataset.chatId = chatId;
        bottomIndicator.dataset.chatType = type;
        bottomIndicator.style.height = '1px';
        bottomIndicator.style.visibility = 'hidden';

        container.appendChild(bottomIndicator);
        this.bottomObserver.observe(bottomIndicator);
    }

    #restoreScrollPosition(chatId, type) {
        if (!this.#application) return;

        const app = this.#application._instance.proxy;
        const container = app.$refs[`${type}Messages`];
        const key = `${type}_${chatId}`;
        const savedPosition = this.scrollPositions.get(key);

        if (container && savedPosition) {
            // Если пользователь был внизу или прокрутил вручную - прокручиваем вниз
            if (savedPosition.isAtBottom || this.isUserScrolled) {
                container.scrollTop = container.scrollHeight;
            } else {
                // Иначе восстанавливаем сохраненную позицию
                container.scrollTop = savedPosition.position;
            }

            this.scrollPositions.delete(key);
        }
    }

    #isAtBottom(container) {
        if (!container) return true;
        const threshold = 100; // 100px от нижнего края
        return container.scrollHeight - container.scrollTop - container.clientHeight <= threshold;
    }

    #updateCounters(total, breakdown) {
        const newState = {
            total,
            private: breakdown.private,
            group: breakdown.group,
            notifications: breakdown.notifications,
            lastUpdate: Date.now(),
        }

        const hasNewUnread = (
            (this.#unreadState.total < total) ||
            (this.#unreadState.private < breakdown.private) ||
            (this.#unreadState.group < breakdown.group) ||
            (this.#unreadState.notifications < breakdown.notifications)
        )

        const prevState = {...this.#unreadState}
        this.#unreadState = newState

        this.#options.onUnreadUpdate?.(
            total,
            newState,
            hasNewUnread,
            prevState,
        )
    }

    async callBitrixAPI(method, data = {}, options = {}) {
        if (!this.#isAuthenticated() && method !== 'auth.check') {
            return {status: 'error', message: 'Not authenticated'}
        }

        const {retryCount = 0, skipSessionCheck = false, cacheTtl = 0} = options
        const cacheKey = `${method}:${JSON.stringify(data)}`;

        if (cacheTtl > 0 && this.#apiCache.has(cacheKey)) {
            const cached = this.#apiCache.get(cacheKey);
            if (Date.now() - cached.timestamp < cacheTtl) {
                return cached.response;
            }
        }

        if (this.#pendingRequests.has(cacheKey)) {
            return this.#pendingRequests.get(cacheKey);
        }

        try {
            const requestPromise = this.#makeBitrixRequest(method, data, {
                retryCount,
                skipSessionCheck
            });

            this.#pendingRequests.set(cacheKey, requestPromise);

            const response = await requestPromise;

            if (cacheTtl > 0 && response.status === 'success') {
                this.#apiCache.set(cacheKey, {
                    response,
                    timestamp: Date.now()
                });
            }

            return response;
        } catch (error) {
            return {status: 'error', message: error.message}
        } finally {
            this.#pendingRequests.delete(cacheKey);
        }
    }

    async #makeBitrixRequest(method, data, options = {}) {
        const {retryCount = 0, skipSessionCheck = false} = options

        return new Promise((resolve) => {
            BX.ajax({
                url: `/api/v1/chat/${method}/`,
                method: 'POST',
                data: {
                    ...data,
                    sessid: BX.bitrix_sessid(),
                },
                onsuccess: (result) => {
                    try {
                        const response = JSON.parse(result);

                        if (this.#isCsrfError(response) && !skipSessionCheck && retryCount < 2) {
                            this.#updateSession().then(() => {
                                this.#makeBitrixRequest(method, data, {
                                    retryCount: retryCount + 1,
                                    skipSessionCheck: true,
                                }).then(resolve);
                            });
                        } else {
                            resolve(response);
                        }
                    } catch {
                        resolve({status: 'error', message: 'Parse error'})
                    }
                },
                onfailure: () => resolve(
                    {status: 'error', message: 'Request failed'}),
            })
        })
    }

    async #updateSession() {
        if (this.#isUpdatingSession) return

        this.#isUpdatingSession = true

        try {
            const response = await this.#makeBitrixRequest('check.sessid')

            if (response.status === 'success') {
                document.cookie = `PHPSESSID=${response.data.sessid}; path=/; SameSite=Lax`
                return true
            }
        } catch (error) {
            console.error('Session update failed:', error)
        } finally {
            this.#isUpdatingSession = false
        }
        return false
    }

    #isCsrfError(response) {
        return response.status === 'error' &&
            (response.message?.includes('csrf') ||
                response.message?.includes('сессия') ||
                response.code === 'invalid_csrf')
    }

    async openPrivateChat(chatId = null) {
        if (!this.#application) return

        const app = this.#application._instance.proxy
        app.isOpen = true
        app.activeTab = 'private'
        app.activePrivateChat = chatId

        if (chatId) {
            this.#startActiveChatUpdates()
        } else {
            this.#stopActiveChatUpdates()
        }

        if (!chatId) return

        try {
            app.isLoading = true
            app.loadingText = 'Загрузка чатов...'

            const response = await this.callBitrixAPI(API_METHODS.RECENT_LIST_ALL)

            if (response.status !== 'success' || !response.data) {
                console.error('Ошибка получения чатов:', response)
                return
            }

            const data = response.data
            app.privateChats = data.chats?.map(chat => ({
                ...chat,
                time: app.formatTime(chat.date),
            })) || []

            if (chatId) {
                await this.#loadMessagesForChat(chatId, 'private', true)
            }
        } catch (error) {
            console.error('Error loading private chat:', error)
        } finally {
            app.isLoading = false
        }
    }

    async openGroupChat(chatId = null) {
        if (!this.#application) return

        const app = this.#application._instance.proxy

        if (chatId && app.activeGroupChat === chatId) {
            app.isOpen = true;
            app.activeTab = 'group';
            return;
        }

        app.isOpen = true
        app.activeTab = 'group'
        app.activeGroupChat = chatId

        if (chatId) {
            this.#startActiveChatUpdates()
        } else {
            this.#stopActiveChatUpdates()
        }

        if (!chatId && this.#wasManuallyOpened && app.groupChats && app.groupChats.length > 0) {
            app.activeGroupChat = app.groupChats[0].id;
            this.#startActiveChatUpdates();
            this.#hasAutoOpenedGroupChat = true;
        }

        try {
            app.isLoading = true
            app.loadingText = 'Загрузка групповых чатов...'

            const response = await this.callBitrixAPI(API_METHODS.RECENT_LIST_ALL)

            if (response.status !== 'success' || !response.data) {
                console.error('Ошибка получения групповых чатов:', response)
                return
            }

            const data = response.data
            app.groupChats = data.groupChats?.map(chat => ({
                ...chat,
                time: app.formatTime(chat.date),
            })) || []

            if (!chatId && this.#wasManuallyOpened && app.groupChats.length === 1) {
                app.activeGroupChat = app.groupChats[0].id;
                this.#startActiveChatUpdates();
                this.#hasAutoOpenedGroupChat = true;
            }

            if (chatId) {
                await this.#loadMessagesForChat(chatId, 'group', true)
            } else if (!chatId && app.groupChats.length === 1) {
                app.activeGroupChat = app.groupChats[0].id;
                this.#startActiveChatUpdates();
            }

            if (app.activeGroupChat) {
                await this.#loadMessagesForChat(app.activeGroupChat, 'group');
            }
        } catch (error) {
            console.error('Error loading group chat:', error)
        } finally {
            app.isLoading = false
        }
    }

    async openNotifications() {
        if (!this.#application) return

        const app = this.#application._instance.proxy
        app.isOpen = true
        app.activeTab = 'notifications'
        this.#stopActiveChatUpdates()

        try {
            app.isLoading = true
            app.loadingText = 'Загрузка уведомлений...'

            const response = await this.callBitrixAPI(API_METHODS.RECENT_LIST_ALL)

            if (response.status === 'success') {
                app.notifications = response.data?.notifications || []
            }
        } catch (error) {
            console.error('Error loading notifications:', error)
        } finally {
            app.isLoading = false
        }
    }

    async openChat(chatId, userId = null, initialMessage = null) {
        if (!this.#application) return

        if (!this.#isAuthenticated()) {
            this.#application?._instance?.proxy?.showError(
                'Для создания чата необходимо авторизоваться')
            return
        }

        if (!chatId) {
            console.error('Chat ID is required')
            return
        }

        const app = this.#application._instance.proxy

        const existingChat = [...app.privateChats, ...app.groupChats].find(
            c => c.id === chatId)
        if (existingChat) {
            this.openPrivateChat(chatId)

            if (initialMessage) {
                setTimeout(() => {
                    this.#sendInitialMessage(chatId, initialMessage)
                }, 500)
            }
            return
        }

        try {
            app.isLoading = true
            app.loadingText = 'Создание нового чата...'

            const tempChat = {
                id: chatId,
                name: 'Новый чат...',
                time: app.formatTime(new Date()),
                unread: 0,
                lastMessage: 'Загрузка...',
                isTemp: true,
                tempId: `temp-${Date.now()}`,
            }

            app.privateChats.unshift(tempChat)
            this.openPrivateChat(chatId)

            const response = await this.callBitrixAPI(API_METHODS.CREATE_PRIVATE_CHAT,
                {
                    USER_ID: userId,
                    CHAT_ID: chatId,
                })

            if (response.status === 'success') {
                const updatedChat = {
                    ...response.data,
                    time: app.formatTime(new Date()),
                    unread: 0,
                    lastMessage: 'Новый чат',
                    isTemp: true,
                }

                const chatIndex = app.privateChats.findIndex(
                    c => c.id === chatId && c.isTemp)
                if (chatIndex > -1) {
                    app.privateChats.splice(chatIndex, 1, updatedChat)
                } else {
                    app.privateChats.unshift(updatedChat)
                }

                app.privateMessages = [
                    {
                        id: 'welcome',
                        text: 'Это начало нового диалога',
                        time: app.formatTime(new Date()),
                        outgoing: false,
                    },
                ]

                if (initialMessage) {
                    setTimeout(() => {
                        this.#sendInitialMessage(chatId, initialMessage)
                    }, 500)
                }

                if (window.ChatManager) {
                    setTimeout(() => {
                        window.ChatManager.refreshAll();
                    }, 500);
                }

                this.#focusMessageInput()
                setTimeout(() => this.checkForUpdates(true), 1000)
            }
        } catch (error) {
            console.error('Error creating chat:', error)
            app.showError('Ошибка при создании чата')
        } finally {
            app.isLoading = false
        }
    }

    async #sendInitialMessage(chatId, messageText) {
        if (!this.#application || !messageText) return

        const app = this.#application._instance.proxy

        try {
            const tempId = 'temp-' + Date.now()
            const newMessage = {
                id: tempId,
                text: messageText,
                time: app.formatTime(new Date()),
                outgoing: true,
            }

            app.privateMessages = [...app.privateMessages, newMessage]

            const chatIndex = app.privateChats.findIndex(c => c.id === chatId)
            if (chatIndex > -1) {
                app.privateChats[chatIndex] = {
                    ...app.privateChats[chatIndex],
                    lastMessage: messageText,
                    date: new Date().toISOString(),
                    unread: 0,
                }
            }

            const response = await this.callBitrixAPI(API_METHODS.MESSAGE_ADD, {
                DIALOG_ID: chatId,
                MESSAGE: messageText,
            })

            if (response.status === 'success' && response.data?.messageId) {
                const messageIndex = app.privateMessages.findIndex(m => m.id === tempId)
                if (messageIndex > -1) {
                    app.privateMessages[messageIndex] = {
                        ...app.privateMessages[messageIndex],
                        id: response.data.messageId,
                    }
                }

                this.#notifyGroupChats({
                    chatId: chatId,
                    message: {
                        id: response.data.messageId,
                        text: messageText,
                        senderId: app.userId,
                        date: new Date().toISOString(),
                    },
                    type: 'private'
                });
            } else {
                throw new Error(response.message || 'Send message failed')
            }

            app.scrollToBottom('privateMessages')

        } catch (error) {
            console.error('Error sending initial message:', error)
            app.showError('Не удалось отправить начальное сообщение')
        }
    }

    async #sendGroupMessageInPage(chatId, messageText, parentMessageId = null) {
        try {
            this.#hasRecentActivity = true;

            const messageData = {
                CHAT_ID: chatId,
                MESSAGE: messageText,
                PARENT_MESSAGE_ID: parentMessageId
            };

            const response = await this.callBitrixAPI(API_METHODS.MESSAGE_ADD, messageData);

            if (response.status === 'success') {
                const newMessage = {
                    id: response.data.messageId,
                    text: messageText,
                    senderId: this.#options.userId || BX.message('USER_ID'),
                    date: new Date().toISOString(),
                    system: false,
                    name: BX.message('USER_NAME') || ''
                };

                // Синхронизируем с групповыми чатами
                this.#synchronizeWithGroupChats({
                    chatId: chatId,
                    message: newMessage,
                    type: 'group'
                });

                if (window.ChatManager) {
                    window.ChatManager.refreshAll();
                }
            }

            return response;
        } catch (error) {
            console.error('Error sending group message:', error);
            throw error;
        }
    }

    async renderGroupChatMessages(chatId, containerSelector = '#group-chat-container') {
        if (!this.#isAuthenticated()) {
            console.error('Пользователь не авторизован');
            return;
        }

        if (!chatId) {
            console.error('Chat ID is required');
            return;
        }

        const container = document.querySelector(containerSelector);
        if (!container) {
            console.error('Container not found:', containerSelector);
            return;
        }

        try {
            const messagesApp = BitrixVue.createApp({
                name: 'GroupChatMessages',
                data() {
                    return {
                        isLoading: true,
                        messages: [],
                        messageText: '',
                        chatId: chatId,
                        userId: this.$options.chatInstance.#options.userId || BX.message('USER_ID') || 0,
                        replyToMessageId: null,
                        privateReplyText: '',
                        groupReplyText: ''
                    };
                },
                computed: {
                    isAuthenticated() {
                        return this.userId > 0;
                    },
                },
                methods: {
                    onVerificationComplete(data) {
                        if (data.verified && data.userId) {
                            this.isVerificationComplete = true;
                            this.verificationUserId = data.userId;
                            this.userId = data.userId;
                            this.showVerification = false;

                            // Обновляем опции чата
                            if (this.$options.chatInstance) {
                                this.$options.chatInstance.#options.userId = data.userId;
                            }

                            // Загружаем данные чата
                            setTimeout(() => {
                                this.loadData();

                                // Запускаем прослушивание обновлений
                                if (this.$options.chatInstance) {
                                    this.$options.chatInstance.startListening();
                                }
                            }, 500);
                        }
                    },
                    showVerificationForm() {
                        this.showVerification = true;
                    },
                    logoutFromVerification() {
                        this.isVerificationComplete = false;
                        this.showVerification = true;
                        this.verificationUserId = 0;
                        this.userId = 0;

                        // Сбрасываем глобальные переменные
                        window.userVerified = false;
                        window.userId = 0;

                        // Останавливаем прослушивание чата
                        if (this.$options.chatInstance) {
                            this.$options.chatInstance.stopListening();
                        }

                        // Очищаем данные чата
                        this.privateChats = [];
                        this.groupChats = [];
                        this.notifications = [];
                        this.privateMessages = [];
                        this.groupMessages = [];
                        this.activePrivateChat = null;
                        this.activeGroupChat = null;
                    },
                    formatBbCode(text, messageId = null) {
                        if (!text) return '';

                        return text
                            .replace(/\[b\](.*?)\[\/b\]/g, '<strong>$1</strong>')
                            .replace(/\[i\](.*?)\[\/i\]/g, '<em>$1</em>')
                            .replace(/\[u\](.*?)\[\/u\]/g, '<u>$1</u>')
                            .replace(/\[s\](.*?)\[\/s\]/g, '<s>$1</s>')
                            .replace(/\[br\]/g, '<br>')
                            .replace(/\[url=(.*?)\](.*?)\[\/url\]/g, '<a href="$1" target="_blank">$2</a>')
                            .replace(/\[url\](.*?)\[\/url\]/g, '<a href="$1" target="_blank">$1</a>')
                            .replace(/\n/g, '<br>');
                    },
                    formatTime(dateString) {
                        if (!dateString) return '';

                        const date = new Date(dateString);
                        if (isNaN(date.getTime())) return '';

                        return date.toLocaleTimeString('ru-RU', {
                            hour: '2-digit',
                            minute: '2-digit',
                            day: '2-digit',
                            month: '2-digit'
                        });
                    },
                    async loadMessages() {
                        try {
                            this.isLoading = true;

                            const response = await this.$options.chatInstance.callBitrixAPI(
                                API_METHODS.DIALOG_MESSAGES_GET,
                                {CHAT_ID: this.chatId}
                            );

                            if (response.status === 'success') {
                                this.messages = (response.data || []).map(msg => ({
                                    ...msg,
                                    time: this.formatTime(msg.date),
                                    outgoing: msg.senderId === this.userId,
                                    isSystem: msg.system === 1,
                                }));
                            }
                        } catch (error) {
                            console.error('Error loading messages:', error);
                        } finally {
                            this.isLoading = false;
                        }
                    },
                    async sendMessage() {
                        if (!this.messageText.trim()) return;

                        try {
                            const response = await this.$options.chatInstance.callBitrixAPI(
                                API_METHODS.MESSAGE_ADD,
                                {
                                    CHAT_ID: this.chatId,
                                    MESSAGE: this.messageText,
                                }
                            );

                            if (response.status === 'success') {
                                this.messageText = '';
                                await this.loadMessages();
                            }
                        } catch (error) {
                            console.error('Error sending message:', error);
                        }
                    },
                    scrollToBottom(ref) {
                        if (!this.#application) return;

                        const app = this.#application._instance.proxy;
                        const container = app.$refs[ref];

                        if (container) {
                            container.scrollTo({
                                top: container.scrollHeight,
                                behavior: 'smooth'
                            });

                            // Сбрасываем счетчик непрочитанных для этого чата
                            const [type, chatId] = ref.replace('Messages', '').split('_');
                            this.unreadMessages.delete(`${type}_${chatId}`);
                        }
                    },
                    handleKeyPress(event) {
                        if (event.key === 'Enter' && !event.shiftKey) {
                            event.preventDefault();
                            this.sendMessage();
                        }
                    },
                    resetScrollBehavior(chatId, type) {
                        const key = `${type}_${chatId}`;
                        if (this.userScrollBehavior) {
                            this.userScrollBehavior.delete(key);
                        }
                    },
                    setupContainerScrollTracking(type, container) {
                        if (!container) return;

                        const handler = () => {
                            const isNearBottom = this.isNearBottom(container);
                            const chatId = type === 'private' ? this.activePrivateChat : this.activeGroupChat;

                            if (chatId) {
                                const key = `${type}_${chatId}`;
                                // Инициализируем userScrollBehavior если нужно
                                if (!this.userScrollBehavior) {
                                    this.userScrollBehavior = new Map();
                                }
                                this.userScrollBehavior.set(key, {
                                    isNearBottom,
                                    lastScrollTime: Date.now(),
                                    manualScroll: !isNearBottom // Если не у низа - значит пользователь прокрутил вручную
                                });
                            }
                        };

                        container.addEventListener('scroll', handler);

                        // Сохраняем обработчик для cleanup
                        if (!this.scrollHandlers) {
                            this.scrollHandlers = new Map();
                        }
                        this.scrollHandlers.set(container, handler);
                    },

                    isNearBottom(container) {
                        if (!container) return true;
                        const threshold = 100; // 100px от нижнего края
                        return container.scrollHeight - container.scrollTop - container.clientHeight <= threshold;
                    },
                },
                mounted() {
                    if (this.isAuthenticated) {
                        setTimeout(() => {
                            this.loadMessages();
                        }, 100);

                        this.updateInterval = setInterval(() => {
                            this.loadMessages();
                        }, 30000);
                    }
                },
                beforeUnmount() {
                    if (this.updateInterval) {
                        clearInterval(this.updateInterval);
                    }
                },
                watch: {
                    messages() {
                        this.scrollToBottom();
                    },
                },
                template: `
                <div class="group-chat-messages">
                    <div v-if="isLoading" class="messages-loading">
                        <div class="loading-spinner"></div>
                        <span>Загрузка сообщений...</span>
                    </div>
                    
                    <div v-else class="messages-container">
                        <div ref="messagesContainer" class="messages-list">
                            <div v-if="messages.length === 0" class="no-messages">
                                <p>Сообщений пока нет</p>
                            </div>
                            
                            <div v-for="message in messages" :key="message.id" 
                                 class="message-item" :class="{ 
                                    'message-outgoing': message.outgoing,
                                    'message-system': message.isSystem,
                                    'message-incoming': !message.outgoing && !message.isSystem
                                 }">
                                 
                                <div v-if="message.isSystem" class="system-message">
                                    <div class="message-content" 
                                         :class="{ truncated: shouldTruncate(message.text) && !isMessageExpanded(message.id) }"
                                         v-html="formatBbCode(message.text, message.id)">
                                    </div>
                                    <div v-if="shouldTruncate(message.text)" class="message-expand">
                                      <button class="expand-btn" @click="toggleMessageExpand(message.id)">
                                        {{ isMessageExpanded(message.id) ? 'Свернуть' : 'Развернуть' }}
                                      </button>
                                    </div>
                                    <div class="message-time">{{ message.time }}</div>
                                </div>
                                
                                <div v-else class="user-message">
                                    <div class="message-header" v-if="message.name">
                                        <span class="sender-name">{{ message.name }}</span>
                                    </div>
                                    <div class="message-content" 
                                         :class="{ truncated: shouldTruncate(message.text) && !isMessageExpanded(message.id) }"
                                         v-html="formatBbCode(message.text, message.id)">
                                    </div>
                                    <div v-if="shouldTruncate(message.text)" class="message-expand">
                                      <button class="expand-btn" @click="toggleMessageExpand(message.id)">
                                        {{ isMessageExpanded(message.id) ? 'Свернуть' : 'Развернуть' }}
                                      </button>
                                    </div>
                                    <div class="message-footer">
                                        <span class="message-time">{{ message.time }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div v-if="isAuthenticated" class="message-input-wrapper">
                        <div class="input-group">
                            <textarea 
                                v-model="messageText" 
                                @keypress="handleKeyPress"
                                placeholder="Введите сообщение..."
                                rows="2"
                                class="message-textarea"
                            ></textarea>
                            <button 
                                @click="sendMessage" 
                                :disabled="!messageText.trim()"
                                class="send-button"
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                                </svg>
                            </button>
                        </div>
                    </div>
                    
                    <div v-else class="auth-required">
                        <p>Для отправки сообщений <a href="/auth/">авторизуйтесь</a></p>
                    </div>
                </div>
            `
            });

            messagesApp.config.globalProperties.$options = {
                chatInstance: this
            };

            return messagesApp.mount(container);

        } catch (error) {
            console.error('Error creating messages renderer:', error);
            throw error;
        }
    }

    #focusMessageInput() {
        setTimeout(() => {
            const input = this.rootNode.querySelector('.message-input input')
            if (input) input.focus()
        }, 300)
    }

    toggleChat() {
        if (this.#application) {
            this.#application.toggleChat()
        }
    }

    closeChat() {
        if (this.#application) {
            const app = this.#application._instance.proxy;
            app.isOpen = false;
            app.activePrivateChat = null;
            app.activeGroupChat = null;
        }

        this.#stopActiveChatUpdates();
        this.#hasAutoOpenedGroupChat = false;
        this.#wasManuallyOpened = false;

        // Останавливаем все интервалы
        if (this.#syncInterval) {
            clearInterval(this.#syncInterval);
            this.#syncInterval = null;
        }
    }

    startAuthChecker() {
        this.authCheckInterval = setInterval(() => {
            const wasAuthenticated = this.#isAuthenticated()
            const isNowAuthenticated = this.#isAuthenticated()

            if (!wasAuthenticated && isNowAuthenticated) {
                this.startListening()
            } else if (wasAuthenticated && !isNowAuthenticated) {
                this.stopListening()
            }
        }, 5000)
    }

    stopAuthChecker() {
        clearInterval(this.authCheckInterval)
    }

    resetScrollBehavior(chatId, type) {
        const key = `${type}_${chatId}`;
        this.userScrollBehavior.delete(key);
    }

    setupScrollTrackingForContainer(type, container) {
        this.#setupContainerScrollTracking(type, container);
    }

    start() {
        return new Promise((resolve, reject) => {
            try {
                this.attachTemplate()
                if (this.#options.autostartListener !== false) {
                    this.startListening()
                }

                setTimeout(() => {
                    const event = new CustomEvent('chatInitialized');
                    document.dispatchEvent(event);
                }, 100);

                resolve(true)
            } catch (e) {
                console.error('Chat initialization failed:', e)
                reject(new Error(`Chat init failed: ${e.message}`))
            }
        })
    }

    attachTemplate() {
        const context = this
        const api = {
            callBitrixAPI: (method, data) => this.callBitrixAPI(method, data),
            getOptions: () => this.#options,
            setApplication: (app) => {
                this.#application = app
            },
            getApplication: () => this.#application,
            forceUpdateUnread: () => this.checkForUpdates(),
            openChat: (chatId, userId) => this.openChat(chatId, userId),
            refreshAllChats: () => this.refreshAllChats(),
            cancelReply: () => this.cancelReply(),
            activateReply: (messageId) => this.activateReply(messageId),
        }

        if (!this.rootNode) {
            console.error('Root node not found')
            return
        }

        const app = BitrixVue.createApp({
            name: 'BitrixChatComponent',
            components: {
                Loader: Loader,
                VerificationManager: VerificationManager,
            },
            chatInstance: context,
            created() {
                // Автопривязка методов
                Object.keys(this).forEach(key => {
                    if (typeof this[key] === 'function') {
                        this[key] = this[key].bind(this);
                    }
                });
            },
            watch: {
                privateChats: {
                    deep: true,
                    handler() {
                        context.#callUnreadCallback()
                    },
                },
                groupChats: {
                    deep: true,
                    handler() {
                        context.#callUnreadCallback()
                    },
                },
                notifications: {
                    deep: true,
                    handler() {
                        context.#callUnreadCallback()
                    },
                },
                activePrivateChat(newVal, oldVal) {
                    if (newVal && this.isOpen) {
                        context.#startActiveChatUpdates();
                        // Настраиваем индикатор для нового чата
                        this.$nextTick(() => {
                            if (this.$refs.privateMessages && newVal) {
                                context.#setupBottomIndicator(this.$refs.privateMessages, 'private', newVal);
                            }
                        });
                    } else if (oldVal && !newVal) {
                        context.#stopActiveChatUpdates();
                    }
                },
                activeGroupChat(newVal, oldVal) {
                    if (newVal && this.isOpen) {
                        context.#startActiveChatUpdates();
                        // Настраиваем индикатор для нового чата
                        this.$nextTick(() => {
                            if (this.$refs.groupMessages && newVal) {
                                context.#setupBottomIndicator(this.$refs.groupMessages, 'group', newVal);
                            }
                        });
                    } else if (oldVal && !newVal) {
                        context.#stopActiveChatUpdates();
                    }
                },
            },
            data() {
                return {
                    isLoading: false,
                    loadingText: 'Загрузка...',
                    isOpen: false,
                    activeTab: 'private',
                    activePrivateChat: null,
                    activeGroupChat: null,
                    privateMessageText: '',
                    groupMessageText: '',
                    privateChats: [],
                    groupChats: [],
                    notifications: [],
                    privateMessages: [],
                    groupMessages: [],
                    updateInterval: null,
                    userId: api.getOptions().userId || BX.message('USER_ID') || 0,
                    isMobile: window.innerWidth <= 768,
                    showToggleButton: context.#options.showToggleButton,
                    sessionInterval: null,
                    authCheckInterval: null,
                    showVerification: false, // Показывать ли блок верификации
                    isVerificationComplete: false, // Завершена ли верификация
                    verificationUserId: 0, // ID пользователя после верификации
                    isUserScrolled: false,
                    scrollDebounce: null,
                    replyToMessageId: null,
                    privateReplyText: '',
                    groupReplyText: '',
                    chatInstance: context,
                    expandedMessages: new Set(),
                    messageTextLimit: 500,
                    userScrollBehavior: new Map(),
                    scrollHandlers: new Map(),
                    scrollThreshold: 100,
                    newMessagesNotifications: {
                        private: {count: 0, chatId: null, visible: false},
                        group: {count: 0, chatId: null, visible: false}
                    },
                    scrollHandlersInitialized: false
                }
            },
            computed: {
                totalUnread() {
                    return this.$options.chatInstance?._unreadState?.total || 0
                },
                isAuthenticated() {
                    return this.userId > 0 || this.isVerificationComplete;
                },
                chatWindowStyle() {
                    if (this.chatInstance.isFullscreen) {
                        return {
                            position: 'fixed',
                            top: '0',
                            left: '0',
                            right: '0',
                            bottom: '0',
                            width: '100vw',
                            height: '100vh',
                            maxHeight: 'none',
                            maxWidth: 'none',
                            zIndex: 9999,
                            background: '#ffffff'
                        }
                    } else if (this.isMobile) {
                        return {
                            position: 'fixed',
                            top: '0',
                            left: '0',
                            right: '0',
                            bottom: '0',
                            width: '100%',
                            height: '100%',
                            maxHeight: 'none',
                        }
                    } else {
                        return {
                            position: 'fixed',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '800px',
                            maxWidth: '90%',
                            maxHeight: '80vh',
                            zIndex: 1000,
                        }
                    }
                },
                showEmptyState() {
                    return this.privateChats.length === 0 && !this.activePrivateChat
                },
                hasUnreadNotifications() {
                    if (this.isLoading) return false
                    return this.notifications.some(n => !n.read && n.unread !== 0)
                },
            },
            methods: {
                onVerificationComplete(data) {
                    if (data.verified && data.userId) {
                        this.isVerificationComplete = true;
                        this.verificationUserId = data.userId;
                        this.userId = data.userId;
                        this.showVerification = false;

                        // Обновляем опции чата
                        if (this.$options.chatInstance) {
                            this.$options.chatInstance.#options.userId = data.userId;
                        }

                        // Загружаем данные чата
                        setTimeout(() => {
                            this.loadData();

                            // Запускаем прослушивание обновлений
                            if (this.$options.chatInstance) {
                                this.$options.chatInstance.startListening();
                            }
                        }, 500);
                    }
                },
                formatBbCode(text, messageId = null) {
                    if (!text) return ''

                    let displayText = text;
                    if (messageId && !this.isMessageExpanded(messageId) && this.shouldTruncate(text)) {
                        displayText = this.getTruncatedText(text);
                    }

                    let html = displayText
                        .replace(/\[b\](.*?)\[\/b\]/g, '<strong>$1</strong>')
                        .replace(/\[i\](.*?)\[\/i\]/g, '<em>$1</em>')
                        .replace(/\[u\](.*?)\[\/u\]/g, '<u>$1</u>')
                        .replace(/\[s\](.*?)\[\/s\]/g, '<s>$1</s>')
                        .replace(/\[br\]/g, '<br>')
                        .replace(/\[url=(.*?)\](.*?)\[\/url\]/g, '<a href="$1" target="_blank" rel="noopener noreferrer">$2</a>')
                        .replace(/\[url\](.*?)\[\/url\]/g, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>')
                        .replace(/\[color=(.*?)\](.*?)\[\/color\]/g, '<span style="color:$1">$2</span>')
                        .replace(/\[size=(.*?)\](.*?)\[\/size\]/g, '<span style="font-size:$1px">$2</span>')
                        .replace(/\[quote\](.*?)\[\/quote\]/gs, '<blockquote>$1</blockquote>')
                        .replace(/\[code\](.*?)\[\/code\]/gs, '<pre><code>$1</code></pre>')
                        .replace(/\[img\](.*?)\[\/img\]/g, '<img src="$1" style="max-width:100%;">')
                        .replace(/\[list\](.*?)\[\/list\]/gs, '<ul>$1</ul>')
                        .replace(/\[\*\](.*?)(\n|$)/g, '<li>$1</li>')
                        .replace(/\n/g, '<br>')

                    return html
                },
                stripBbCode(text) {
                    if (!text) return ''

                    return text.replace(/\[b\](.*?)\[\/b\]/g, '$1').replace(/\[i\](.*?)\[\/i\]/g, '$1').replace(/\[u\](.*?)\[\/u\]/g, '$1').replace(/\[s\](.*?)\[\/s\]/g, '$1').replace(/\[br\]/g, ' ').replace(/\[url=(.*?)\](.*?)\[\/url\]/g, '$2').replace(/\[url\](.*?)\[\/url\]/g, '$1').replace(/\[color=(.*?)\](.*?)\[\/color\]/g, '$2').replace(/\[size=(.*?)\](.*?)\[\/size\]/g, '$2').replace(/\[quote\](.*?)\[\/quote\]/gs, '$1').replace(/\[code\](.*?)\[\/code\]/gs, '$1').replace(/\[img\](.*?)\[\/img\]/g, '📷').replace(/\[list\](.*?)\[\/list\]/gs, '$1').replace(/\[\*\](.*?)(\n|$)/g, '$1').replace(/\n/g, ' ')
                },
                sendMessageWrapper() {
                    const chat = this.$options.chatInstance
                    if (chat && chat.sendPrivateMessage) {
                        chat.sendPrivateMessage()
                    }
                },
                checkAuthStatus() {
                    const userId = this.userId || BX.message('USER_ID')
                    return !!userId && parseInt(userId) > 0
                },
                showLoader(text = '') {
                    this.isLoading = true
                    this.loadingText = text || 'Загрузка...'
                },
                hideLoader() {
                    this.isLoading = false
                },
                toggleChat() {
                    this.isOpen = !this.isOpen
                    if (this.isOpen) {
                        this.loadData()

                        if (this.groupChats.length === 1 && !this.activeGroupChat && !this.activePrivateChat) {
                            this.selectGroupChat(this.groupChats[0].id);
                        }
                    } else {
                        this.activePrivateChat = null;
                        this.activeGroupChat = null;
                        if (this.$options.chatInstance) {
                            this.$options.chatInstance.#stopActiveChatUpdates();
                            this.$options.chatInstance.#hasAutoOpenedGroupChat = false;
                            this.$options.chatInstance.#wasManuallyOpened = false;
                        }
                    }
                },
                closeChat() {
                    this.isOpen = false
                },
                setActiveTab(tab) {
                    this.activeTab = tab

                    if (tab === 'group' &&
                        // this.#wasManuallyOpened &&
                        this.groupChats.length === 1 &&
                        !this.activeGroupChat) {

                        setTimeout(() => {
                            this.selectGroupChat(this.groupChats[0].id);
                            this.#hasAutoOpenedGroupChat = true;
                        }, 100);
                    }

                    // Сбрасываем активный чат при переходе на другие вкладки
                    if (tab !== 'group') {
                        this.activeGroupChat = null;
                    }
                    if (tab !== 'private') {
                        this.activePrivateChat = null;
                    }
                },
                selectPrivateChat(chatId) {
                    this.activePrivateChat = chatId;

                    // Сбрасываем флаг загрузки
                    const chatInstance = this.getChatInstance();
                    if (chatInstance && typeof chatInstance.resetInitialLoad === 'function') {
                        chatInstance.resetInitialLoad(chatId, 'private');
                    }

                    // Загружаем сообщения
                    if (chatInstance && typeof chatInstance.loadMessagesForChat === 'function') {
                        chatInstance.loadMessagesForChat(chatId, 'private');
                    }
                },
                selectGroupChat(chatId) {
                    if (this.activeGroupChat === chatId) return;
                    this.activeGroupChat = chatId;

                    // Сбрасываем флаг загрузки
                    const chatInstance = this.getChatInstance();
                    if (chatInstance && typeof chatInstance.resetInitialLoad === 'function') {
                        chatInstance.resetInitialLoad(chatId, 'group');
                    }

                    // Загружаем сообщения
                    if (chatInstance && typeof chatInstance.loadMessagesForChat === 'function') {
                        chatInstance.loadMessagesForChat(chatId, 'group');
                    }
                },
                updateLastMessageInChats(chatId, message) {
                    const chatIndex = this.privateChats.findIndex(c => c.id === chatId)

                    if (chatIndex > -1) {
                        const prevState = {
                            lastMessage: this.stripBbCode(message),
                            date: this.privateChats[chatIndex].date,
                        }

                        this.privateChats[chatIndex] = {
                            ...this.privateChats[chatIndex],
                            lastMessage: message,
                            date: new Date().toISOString(),
                            unread: 0,
                            __prevState: prevState,
                        }

                        this.privateChats.unshift(this.privateChats.splice(chatIndex, 1)[0])
                    }

                    this.privateChats[0].__animate = true
                    setTimeout(() => {
                        if (this.privateChats[0]) {
                            this.privateChats[0].__animate = false
                        }
                    }, 300)
                },
                revertLastMessageUpdate(chatId) {
                    const chatIndex = this.privateChats.findIndex(c => c.id === chatId)

                    if (chatIndex > -1 && this.privateChats[chatIndex].__prevState) {
                        this.privateChats[chatIndex] = {
                            ...this.privateChats[chatIndex],
                            lastMessage: this.privateChats[chatIndex].__prevState.lastMessage,
                            date: this.privateChats[chatIndex].__prevState.date,
                        }
                    }
                },
                async sendPrivateMessage() {
                    if (!this.activePrivateChat || !this.privateMessageText.trim()) return

                    const messageText = this.privateMessageText
                    const chatId = this.activePrivateChat

                    try {
                        const tempId = 'temp-' + Date.now()
                        const newMessage = {
                            id: tempId,
                            text: messageText,
                            time: this.formatTime(new Date()),
                            outgoing: true,
                        }

                        this.updateUIBeforeSend(chatId, newMessage, messageText)

                        const response = await context.callBitrixAPI(
                            API_METHODS.MESSAGE_ADD, {
                                DIALOG_ID: chatId,
                                MESSAGE: messageText,
                            })

                        this.handleSendResponse(chatId, tempId, response)

                        this.$nextTick(() => {
                            const container = this.$refs.privateMessages;
                            if (container) {
                                const isNearBottom = this.isNearBottom(container);
                                if (!isNearBottom) {
                                    this.showNewMessagesNotification('private', chatId, 1);
                                }
                            }
                        });


                    } catch (error) {
                        console.error('Error sending message:', error)
                        this.handleSendError(chatId)
                    } finally {
                        this.privateMessageText = ''
                    }
                },
                updateUIBeforeSend(chatId, newMessage, messageText) {
                    this.privateMessages = [...this.privateMessages, newMessage]

                    const chatIndex = this.privateChats.findIndex(c => c.id === chatId)
                    if (chatIndex > -1) {
                        const updatedChat = {
                            ...this.privateChats[chatIndex],
                            lastMessage: messageText,
                            date: new Date().toISOString(),
                            unread: 0,
                        }

                        this.privateChats = [
                            updatedChat,
                            ...this.privateChats.filter(c => c.id !== chatId),
                        ]
                    }

                    this.$options.chatInstance.scrollToBottom('privateMessages')
                },
                handleSendResponse(chatId, tempId, response) {
                    if (response.status === 'success' && response.data?.messageId) {
                        const messageIndex = this.privateMessages.findIndex(
                            m => m.id === tempId)
                        if (messageIndex > -1) {
                            this.privateMessages[messageIndex] = {
                                ...this.privateMessages[messageIndex],
                                id: response.data.messageId,
                            }
                        }

                        const chatIndex = this.privateChats.findIndex(c => c.id === chatId)
                        if (chatIndex > -1) {
                            this.privateChats[chatIndex] = {
                                ...this.privateChats[chatIndex],
                                lastMessage: this.privateMessageText,
                                date: new Date().toISOString(),
                                unread: 0,
                            }
                        }

                        this.$options.chatInstance.scrollToBottom('privateMessages')

                    } else {
                        throw new Error(response.message || 'Send message failed')
                    }
                },
                handleSendError(chatId) {
                    this.showError('Не удалось отправить сообщение')
                    this.revertLastMessageUpdate(chatId)
                    this.privateMessages = this.privateMessages.filter(
                        m => !m.id.startsWith('temp-'))
                },
                async sendGroupMessage() {
                    if (!this.groupMessageText.trim()) return;
                    if (!this.activeGroupChat) return;

                    const messageData = {
                        CHAT_ID: this.activeGroupChat,
                        MESSAGE: this.groupMessageText
                    };

                    if (this.replyToMessageId) {
                        messageData.PARENT_MESSAGE_ID = this.replyToMessageId;
                    }

                    try {
                        const response = await this.$options.chatInstance.callBitrixAPI(
                            API_METHODS.MESSAGE_ADD, messageData);

                        if (response.status === 'success') {
                            this.groupMessageText = '';
                            this.replyToMessageId = null;
                            this.groupReplyText = '';
                            await this.$options.chatInstance.loadMessagesForChat(this.activeGroupChat, 'group');
                        }
                    } catch (error) {
                        console.error('Error sending group message:', error);
                        this.showError('Ошибка при отправке сообщения');
                    }
                },
                async markAsRead(notificationId) {
                    try {
                        const response = await context.callBitrixAPI(
                            API_METHODS.NOTIFY_READ, {
                                ID: notificationId,
                            })

                        if (response.status === 'success') {
                            const index = this.notifications.findIndex(
                                n => n.id === notificationId)
                            if (index !== -1) {
                                this.notifications[index] = {
                                    ...this.notifications[index],
                                    read: true,
                                    unread: 0,
                                }
                            }
                        } else {
                            console.error('Error marking notification as read:',
                                response.message)
                        }
                    } catch (error) {
                        console.error('Error marking notification as read:', error)
                    }
                },
                async markAllNotificationsAsRead() {
                    if (this.isLoading) return

                    try {
                        this.isLoading = true
                        const response = await context.callBitrixAPI(
                            API_METHODS.NOTIFY_READ, {'IDS': [0]})

                        if (response.status === 'success') {
                            this.notifications = this.notifications.map(n => ({
                                ...n,
                                read: true,
                                unread: 0,
                            }))
                        } else {
                            console.error('Error marking all notifications as read:',
                                response.message)
                        }
                    } catch (error) {
                        console.error('Error marking all notifications as read:', error)
                    } finally {
                        this.isLoading = false
                    }
                },
                getPrivateChat(chatId) {
                    return this.privateChats.find(c => c.id === chatId)
                },
                getGroupChat(chatId) {
                    return this.groupChats.find(c => c.id === chatId)
                },
                getGroupChatStyle(chatId) {
                    const chat = this.getGroupChat(chatId);
                    let color;

                    if (chat && chat.color) {
                        color = chat.color;
                    } else {
                        color = this.$options.chatInstance.#getDeterministicColor(chatId, 'group');
                    }

                    return {
                        backgroundColor: color,
                        color: this.getContrastColor(color, 'text'),
                        borderLeft: `3px solid ${color}`
                    };
                },

                getPrivateChatStyle(chatId) {
                    const chat = this.getPrivateChat(chatId);
                    let color;

                    if (chat && chat.color) {
                        color = chat.color;
                    } else {
                        color = this.$options.chatInstance.#getDeterministicColor(chatId, 'private');
                    }

                    return {
                        backgroundColor: color,
                        color: this.getContrastColor(color, 'text'),
                        borderLeft: `3px solid ${color}`
                    };
                },

                getGroupLinkStyle(chatId) {
                    const chat = this.getGroupChat(chatId);
                    let color;

                    if (chat && chat.color) {
                        color = chat.color;
                    } else {
                        color = this.$options.chatInstance.#getDeterministicColor(chatId, 'group');
                    }

                    return {
                        color: this.getContrastColor(color, 'link'),
                        fontWeight: '500'
                    };
                },

                getNotificationIcon(type) {
                    switch (type) {
                        case 'system':
                            return 'notification-icon system'
                        case 'personal':
                            return 'notification-icon personal'
                        case 'confirm':
                            return 'notification-icon confirm'
                        default:
                            return 'notification-icon default'
                    }
                },
                formatTime(date) {
                    if (!date) return ''

                    if (typeof date === 'string') {
                        return this.formatMessageDate(date)
                    } else {
                        date = new Date(date)
                        if (isNaN(date.getTime())) {
                            return ''
                        }

                        return date.toLocaleTimeString([], {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                        })
                    }
                },
                getMonthName(monthIndex) {
                    const months = [
                        'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
                        'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
                    ]
                    return months[monthIndex]
                },
                formatMessageDate(dateString) {
                    if (!dateString) return ''

                    const parseCustomDate = (str) => {
                        const [datePart, timePart] = str.split(' ')
                        const [day, month, year] = datePart.split('.')
                        return new Date(`${year}-${month}-${day}T${timePart}`)
                    }

                    const date = typeof dateString === 'string'
                        ? parseCustomDate(dateString)
                        : new Date(dateString)

                    if (isNaN(date.getTime())) {
                        console.error('Invalid date:', dateString)
                        return ''
                    }

                    const today = new Date()
                    today.setHours(0, 0, 0, 0)

                    const yesterday = new Date(today)
                    yesterday.setDate(yesterday.getDate() - 1)

                    const dayBeforeYesterday = new Date(today)
                    dayBeforeYesterday.setDate(dayBeforeYesterday.getDate() - 2)

                    const timeStr = date.toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                    })

                    if (date >= today) {
                        return timeStr
                    } else if (date >= yesterday) {
                        return `вчера в ${timeStr}`
                    } else if (date >= dayBeforeYesterday) {
                        return `позавчера в ${timeStr}`
                    } else {
                        return `${date.getDate()} ${this.getMonthName(
                            date.getMonth())} в ${timeStr}`
                    }
                },
                getContrastColor(hexColor, type = 'text') {
                    if (!hexColor) return type === 'text' ? '#333333' : '#007bff';

                    // Нормализуем hex цвет
                    hexColor = hexColor.replace('#', '');
                    if (hexColor.length === 3) {
                        hexColor = hexColor.split('').map(char => char + char).join('');
                    }

                    // Конвертируем HEX в RGB
                    const r = parseInt(hexColor.substr(0, 2), 16) / 255;
                    const g = parseInt(hexColor.substr(2, 2), 16) / 255;
                    const b = parseInt(hexColor.substr(4, 2), 16) / 255;

                    // Рассчитываем относительную яркость по формуле WCAG 2.1
                    const rsrgb = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4);
                    const gsrgb = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4);
                    const bsrgb = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4);

                    const luminance = 0.2126 * rsrgb + 0.7152 * gsrgb + 0.0722 * bsrgb;

                    // Для текста используем черный или белый в зависимости от контраста
                    if (type === 'text') {
                        // Рассчитываем контраст с черным и белым
                        const contrastWithBlack = (luminance + 0.05) / 0.05;
                        const contrastWithWhite = 1.05 / (luminance + 0.05);

                        // Выбираем цвет с лучшим контрастом (минимум 4.5:1 для нормального текста)
                        if (contrastWithWhite > contrastWithBlack && contrastWithWhite >= 4.5) {
                            return '#ffffff'; // Белый текст
                        } else {
                            return '#000000'; // Черный текст
                        }
                    }

                    // Для ссылок используем более контрастные варианты
                    if (type === 'link') {
                        if (luminance > 0.6) {
                            return '#0056b3'; // Темно-синий для светлых фонов
                        } else {
                            return '#ffffff'; // Белый для темных фонов
                        }
                    }

                    return luminance > 0.5 ? '#000000' : '#ffffff';
                },
                scrollToBottom(ref) {
                    if (!this.#application) return;

                    const app = this.#application._instance.proxy;
                    const container = app.$refs[ref];

                    if (container) {
                        container.scrollTo({
                            top: container.scrollHeight,
                            behavior: 'smooth'
                        });

                        // Сбрасываем счетчик непрочитанных для этого чата
                        const [type, chatId] = ref.replace('Messages', '').split('_');
                        this.unreadMessages.delete(`${type}_${chatId}`);
                        this.#hideNewMessagesNotification(type, chatId);
                    }
                },
                showError(message) {
                    BX.UI.Notification.Center.notify({
                        content: message,
                        autoHideDelay: 3000,
                    })
                },
                async loadData() {
                    if (!this.userId) return
                    try {
                        const response = await context.callBitrixAPI(
                            API_METHODS.RECENT_LIST_ALL)

                        if (response.status !== 'success') {
                            throw new Error(response.message || 'Не удалось загрузить данные')
                        }

                        const data = response.data

                        switch (this.activeTab) {
                            case 'private':
                                this.privateChats = data.chats?.map(chat => ({
                                    ...chat,
                                    time: this.formatTime(chat.date),
                                })) || []
                                if (this.activePrivateChat) {
                                    await context.#loadMessagesForChat(this.activePrivateChat,
                                        'private')
                                }
                                break

                            case 'group':
                                this.groupChats = data.groupChats?.map(chat => ({
                                    ...chat,
                                    time: this.formatTime(chat.date),
                                })) || []
                                if (this.groupChats.length === 1 && !this.activeGroupChat && this.isOpen) {
                                    this.selectGroupChat(this.groupChats[0].id);
                                } else if (this.activeGroupChat) {
                                    await context.#loadMessagesForChat(this.activeGroupChat, 'group');
                                }
                                break

                            case 'notifications':
                                this.notifications = data.notifications?.map(notif => ({
                                    ...notif,
                                    time: this.formatTime(notif.date),
                                    read: Boolean(notif.read || notif.unread === 0),
                                })) || []
                                break
                        }
                    } catch (error) {
                        console.error('Error loading data:', error)
                        this.showError('Ошибка загрузки данных')
                    }
                },
                async loadNotifications() {
                    try {
                        const response = await context.callBitrixAPI(
                            API_METHODS.RECENT_LIST_ALL)

                        if (response.status === 'success') {
                            this.notifications = response.data?.notifications || []
                        } else {
                            console.error('Error loading notifications:', response.message)
                        }
                    } catch (error) {
                        console.error('Error loading notifications:', error)
                    }
                },
                handleResize() {
                    this.isMobile = window.innerWidth <= 768
                },
                setupScrollHandlers() {
                    const containers = [this.$refs.privateMessages, this.$refs.groupMessages].filter(Boolean);

                    containers.forEach(container => {
                        if (container) {
                            container.addEventListener('scroll', this.handleScroll);
                        }
                    });
                },
                activateReply(messageId) {
                    // Простой вызов без приватных полей
                    const chatInstance = this.getChatInstance();
                    if (chatInstance && chatInstance.activateReply) {
                        chatInstance.activateReply(messageId);
                    }
                },
                cancelReply() {
                    const chatInstance = this.getChatInstance();
                    if (chatInstance && chatInstance.cancelReply) {
                        chatInstance.cancelReply();
                    }
                },
                getChatInstance() {
                    const instance = this.$options.chatInstance;
                    // Проверяем, что экземпляр существует и имеет нужные методы
                    if (instance && typeof instance.loadMessagesForChat === 'function') {
                        return instance;
                    }
                    return null;
                },
                safeScrollToBottom(ref) {
                    const chatInstance = this.getChatInstance();
                    if (chatInstance && typeof chatInstance.scrollToBottom === 'function') {
                        chatInstance.scrollToBottom(ref);
                    }
                },
                shouldTruncate(text) {
                    return text && text.length > this.messageTextLimit;
                },
                getTruncatedText(text) {
                    if (!text) return '';
                    if (text.length <= this.messageTextLimit) return text;

                    return text.substring(0, this.messageTextLimit) + '...';
                },
                toggleMessageExpand(messageId) {
                    if (this.expandedMessages.has(messageId)) {
                        this.expandedMessages.delete(messageId);
                    } else {
                        this.expandedMessages.add(messageId);
                    }
                },
                isMessageExpanded(messageId) {
                    return this.expandedMessages.has(messageId);
                },
                resetScrollBehavior(chatId, type) {
                    const key = `${type}_${chatId}`;
                    if (this.userScrollBehavior) {
                        this.userScrollBehavior.delete(key);
                    }
                },
                setupContainerScrollTracking(type, container) {
                    if (!container) return;

                    const handler = () => {
                        const isNearBottom = this.isNearBottom(container);
                        const chatId = type === 'private' ? this.activePrivateChat : this.activeGroupChat;

                        if (chatId) {
                            const key = `${type}_${chatId}`;
                            // Инициализируем userScrollBehavior если нужно
                            if (!this.userScrollBehavior) {
                                this.userScrollBehavior = new Map();
                            }
                            this.userScrollBehavior.set(key, {
                                isNearBottom,
                                lastScrollTime: Date.now(),
                                manualScroll: !isNearBottom // Если не у низа - значит пользователь прокрутил вручную
                            });
                        }
                    };

                    container.addEventListener('scroll', handler);

                    // Сохраняем обработчик для cleanup
                    if (!this.scrollHandlers) {
                        this.scrollHandlers = new Map();
                    }
                    this.scrollHandlers.set(container, handler);
                },
                showNewMessagesNotification(type, chatId, count = 1) {
                    if (this.activeTab !== type) return;

                    const currentChatId = type === 'private' ? this.activePrivateChat : this.activeGroupChat;
                    if (currentChatId !== chatId) return;

                    const container = this.$refs[`${type}Messages`];
                    if (!container) return;

                    const isNearBottom = this.isNearBottom(container);
                    if (isNearBottom) return; // Не показываем если пользователь внизу

                    this.newMessagesNotifications[type] = {
                        count: (this.newMessagesNotifications[type].count || 0) + count,
                        chatId: chatId,
                        visible: true
                    };
                },
                hideNewMessagesNotification(type) {
                    this.newMessagesNotifications[type] = {
                        count: 0,
                        chatId: null,
                        visible: false
                    };
                },
                scrollToBottomWithNotification(type) {
                    const chatInstance = this.getChatInstance();
                    if (chatInstance && typeof chatInstance.safeScrollToBottom === 'function') {
                        chatInstance.safeScrollToBottom(`${type}Messages`);
                    }
                    this.hideNewMessagesNotification(type);
                },
                setupScrollTracking() {
                    if (this.scrollHandlersInitialized) return;

                    ['private', 'group'].forEach(type => {
                        const container = this.$refs[`${type}Messages`];
                        if (container) {
                            container.addEventListener('scroll', () => {
                                this.handleScroll(type, container);
                            });
                        }
                    });

                    this.scrollHandlersInitialized = true;
                },
                handleScroll(type, container) {
                    if (!container) return;

                    const isNearBottom = this.isNearBottom(container);

                    // Скрываем уведомление если пользователь прокрутил вниз
                    if (isNearBottom && this.newMessagesNotifications[type].visible) {
                        this.hideNewMessagesNotification(type);
                    }

                    // Отмечаем видимые сообщения как прочитанные
                    if (!isNearBottom) {
                        const chatInstance = this.getChatInstance();
                        if (chatInstance && typeof chatInstance.markVisibleMessagesAsRead === 'function') {
                            chatInstance.markVisibleMessagesAsRead();
                        }
                    }

                    // Сохраняем поведение прокрутки
                    const chatId = type === 'private' ? this.activePrivateChat : this.activeGroupChat;
                    if (chatId) {
                        const key = `${type}_${chatId}`;
                        this.userScrollBehavior.set(key, {
                            isNearBottom,
                            lastScrollTime: Date.now(),
                            manualScroll: !isNearBottom
                        });
                    }
                },
                isNearBottom(container) {
                    if (!container) return true;
                    const threshold = 100;
                    return container.scrollHeight - container.scrollTop - container.clientHeight <= threshold;
                },
                // Показать форму верификации
                showVerificationForm() {
                    this.showVerification = true;
                    this.isVerificationComplete = false;
                },

                // Инициализация чата для верифицированного пользователя
                initializeChatForVerifiedUser() {
                    // Обновляем userId в компоненте
                    this.userId = this.verificationUserId || window.userId || 0;

                    // Обновляем опции чата
                    if (this.$options.chatInstance) {
                        this.$options.chatInstance.#options.userId = this.userId;
                    }

                    // Загружаем данные чата
                    this.loadData();

                    // Запускаем прослушивание обновлений
                    if (this.$options.chatInstance) {
                        this.$options.chatInstance.startListening();
                    }
                },

                // Выйти из верифицированного режима
                logoutFromVerification() {
                    this.isVerificationComplete = false;
                    this.showVerification = true;
                    this.verificationUserId = 0;
                    this.userId = 0;

                    // Сбрасываем глобальные переменные
                    window.userVerified = false;
                    window.userId = 0;

                    // Останавливаем прослушивание чата
                    if (this.$options.chatInstance) {
                        this.$options.chatInstance.stopListening();
                    }

                    // Очищаем данные чата
                    this.privateChats = [];
                    this.groupChats = [];
                    this.notifications = [];
                    this.privateMessages = [];
                    this.groupMessages = [];
                    this.activePrivateChat = null;
                    this.activeGroupChat = null;
                },
            },
            mounted() {
                this.loadData()
                window.addEventListener('resize', this.handleResize)
                this.$nextTick(() => {
                    this.setupScrollTracking(); // Инициализируем отслеживание скролла
                });

                // Проверяем, не верифицирован ли пользователь через VerificationManager
                if (!this.userId || this.userId <= 0) {
                    this.showVerification = true;
                }

                // Проверяем, не верифицирован ли пользователь через VerificationManager
                if (typeof window.userVerified !== 'undefined' && window.userVerified && window.userId > 0) {
                    this.isVerificationComplete = true;
                    this.verificationUserId = window.userId;
                    this.userId = window.userId;
                    this.showVerification = false;

                    // Инициализируем чат для верифицированного пользователя
                    setTimeout(() => {
                        this.loadData();
                        if (this.$options.chatInstance) {
                            this.$options.chatInstance.startListening();
                        }
                    }, 100);
                }

                // В полноэкранном режиме автоматически открываем чат
                if (this.chatInstance.isFullscreen) {
                    this.isOpen = true;
                    this.toggleChat();
                }
            },
            beforeUnmount() {
                window.removeEventListener('resize', this.handleResize)
                const containers = [this.$refs.privateMessages, this.$refs.groupMessages].filter(Boolean);
                containers.forEach(container => {
                    if (container) {
                        container.removeEventListener('scroll', this.handleScroll);
                    }
                });
                if (this.scrollHandlers) {
                    this.scrollHandlers.forEach((handler, container) => {
                        if (container && container.removeEventListener) {
                            container.removeEventListener('scroll', handler);
                        }
                    });
                    this.scrollHandlers.clear();
                }
            },
            template: `
                <div class="bitrix-chat-container" :class="{ 'fullscreen-mode': chatInstance.isFullscreen }">
                  <div class="chat-toggle" @click="toggleChat" v-if="showToggleButton && !chatInstance.isFullscreen">
                    <i class="fas fa-comments"></i>
                    <span class="unread-count" v-if="totalUnread > 0">{{ totalUnread }}</span>
                  </div>
                  
                  <div class="chat-window" v-if="isOpen || chatInstance.isFullscreen" :style="chatWindowStyle">
                    <div class="chat-header">
                      <h5>Мессенджер</h5>
                      <button class="close-btn" @click="closeChat" v-if="!chatInstance.isFullscreen">&times;</button>
                    </div>
                    
                    <div class="tabs-header" v-if="isAuthenticated">
                      <button @click="setActiveTab('private')" 
                          :class="{ active: activeTab === 'private' }"
                          class="tab-button">
                        <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path fill-rule="evenodd" clip-rule="evenodd" d="M2.9 2.32851C2.68783 2.32851 2.48434 2.4128 2.33431 2.56283C2.18429 2.71286 2.1 2.91634 2.1 3.12851V13.9971L3.93431 12.1628C4.08434 12.0128 4.28783 11.9285 4.5 11.9285H14.1C14.3122 11.9285 14.5157 11.8442 14.6657 11.6942C14.8157 11.5442 14.9 11.3407 14.9 11.1285V3.12851C14.9 2.91634 14.8157 2.71286 14.6657 2.56283C14.5157 2.4128 14.3122 2.32851 14.1 2.32851H2.9ZM1.20294 1.43146C1.65303 0.981372 2.26348 0.728516 2.9 0.728516H14.1C14.7365 0.728516 15.347 0.981372 15.7971 1.43146C16.2471 1.88154 16.5 2.49199 16.5 3.12851V11.1285C16.5 11.765 16.2471 12.3755 15.7971 12.8256C15.347 13.2756 14.7365 13.5285 14.1 13.5285H4.83137L1.86569 16.4942C1.63689 16.723 1.29279 16.7914 0.993853 16.6676C0.694914 16.5438 0.5 16.2521 0.5 15.9285V3.12851C0.5 2.49199 0.752856 1.88155 1.20294 1.43146Z" fill="#484444"/>
                        </svg>
                        <span>Личные чаты</span>
                      </button>
                      
                      <button @click="setActiveTab('group')" 
                          :class="{ active: activeTab === 'group' }"
                          class="tab-button">
                        <svg width="14" height="17" viewBox="0 0 14 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M10.6718 11.0126C11.351 10.2809 11.805 9.36238 11.9777 8.37047C12.1504 7.37856 12.0343 6.35678 11.6437 5.43143C11.253 4.50609 10.605 3.71773 9.77974 3.16381C8.95445 2.60989 7.98805 2.31468 7 2.31468C6.01195 2.31468 5.04555 2.60989 4.22026 3.16381C3.39496 3.71773 2.74695 4.50609 2.35633 5.43143C1.9657 6.35678 1.84957 7.37856 2.0223 8.37047C2.19503 9.36238 2.64904 10.2809 3.32822 11.0126C3.71798 10.3785 4.25943 9.85576 4.90171 9.49354C5.54398 9.13133 6.26602 8.94151 7 8.94192C7.73398 8.94151 8.45602 9.13133 9.09829 9.49354C9.74057 9.85576 10.282 10.3785 10.6718 11.0126ZM4.38917 12.0973L7 14.7586L9.61083 12.0973C9.3767 11.5933 9.00705 11.1674 8.54487 10.8692C8.08268 10.571 7.54696 10.4126 7 10.4126C6.45304 10.4126 5.91732 10.571 5.45513 10.8692C4.99295 11.1674 4.6233 11.5933 4.38917 12.0973ZM7 16.8418L2.40378 12.1555C1.49475 11.2286 0.87569 10.0477 0.624893 8.76213C0.374095 7.47654 0.502822 6.14399 0.994793 4.93299C1.48677 3.72199 2.31989 2.68694 3.3888 1.95871C4.45772 1.23049 5.71443 0.841797 7 0.841797C8.28557 0.841797 9.54228 1.23049 10.6112 1.95871C11.6801 2.68694 12.5132 3.72199 13.0052 4.93299C13.4972 6.14399 13.6259 7.47654 13.3751 8.76213C13.1243 10.0477 12.5053 11.2286 11.5962 12.1555L7 16.8418ZM7 6.73278C7.19155 6.73278 7.37525 6.65519 7.51069 6.5171C7.64613 6.379 7.72222 6.1917 7.72222 5.9964C7.72222 5.8011 7.64613 5.6138 7.51069 5.4757C7.37525 5.3376 7.19155 5.26002 7 5.26002C6.80845 5.26002 6.62475 5.3376 6.48931 5.4757C6.35387 5.6138 6.27778 5.8011 6.27778 5.9964C6.27778 6.1917 6.35387 6.379 6.48931 6.5171C6.62475 6.65519 6.80845 6.73278 7 6.73278ZM7 8.20554C6.42536 8.20554 5.87426 7.97279 5.46794 7.55849C5.06161 7.1442 4.83333 6.5823 4.83333 5.9964C4.83333 5.4105 5.06161 4.84859 5.46794 4.4343C5.87426 4.02001 6.42536 3.78726 7 3.78726C7.57464 3.78726 8.12574 4.02001 8.53206 4.4343C8.93839 4.84859 9.16667 5.4105 9.16667 5.9964C9.16667 6.5823 8.93839 7.1442 8.53206 7.55849C8.12574 7.97279 7.57464 8.20554 7 8.20554Z" fill="#484444"/>
                        </svg>
                        <span>Групповые чаты</span>
                      </button>
                      
                      <button @click="setActiveTab('notifications')" 
                          :class="{ active: activeTab === 'notifications' }"
                          class="tab-button">
                        <svg width="14" height="17" viewBox="0 0 14 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path fill-rule="evenodd" clip-rule="evenodd" d="M6.99986 0.917969C7.37354 0.917969 7.67647 1.24174 7.67647 1.64113V2.4137C8.37528 2.51462 9.21644 2.79095 9.95998 3.42671C10.9956 4.31218 11.7363 5.80395 11.7363 8.14957C11.7363 10.0042 12.2666 11.4717 13.2291 12.3989C13.511 12.6705 13.5483 13.0609 13.4527 13.3499C13.3557 13.6431 13.0845 13.9349 12.6836 13.9349H1.31644C0.915454 13.9349 0.6443 13.6431 0.547283 13.3499C0.451684 13.0609 0.489022 12.6705 0.770916 12.3989C1.73342 11.4717 2.2637 10.0042 2.2637 8.14957C2.2637 5.80395 3.00443 4.31218 4.04002 3.42671C4.78346 2.79104 5.62449 2.5147 6.32324 2.41374V1.64113C6.32324 1.24174 6.62617 0.917969 6.99986 0.917969ZM4.88538 4.55609C4.22943 5.11694 3.61693 6.15623 3.61693 8.14957C3.61693 9.79016 3.25446 11.2984 2.48506 12.4885H11.5149C10.7455 11.2984 10.3831 9.79016 10.3831 8.14957C10.3831 6.15623 9.77057 5.11694 9.11463 4.55609C8.42919 3.97002 7.58417 3.81061 7 3.81061C6.41583 3.81061 5.57081 3.97002 4.88538 4.55609Z" fill="#484444"/>
                          <path d="M6.20968 14.9801C6.00239 14.6478 5.5823 14.558 5.27138 14.7795C4.96046 15.0011 4.87644 15.4501 5.08372 15.7824C6.02815 17.2965 7.9717 17.2965 8.91613 15.7824C9.12341 15.4501 9.0394 15.0011 8.72847 14.7795C8.41755 14.558 7.99746 14.6478 7.79018 14.9801C7.38138 15.6355 6.61847 15.6355 6.20968 14.9801Z" fill="#484444"/>
                        </svg>
                        <span>Уведомления</span>
                      </button>
                    </div>
                    
                    <div class="chat-content" v-if="isAuthenticated">
                      <Loader v-if="isLoading" />
        
                      <div :class="['tab-content', { active: activeTab === 'private' }]" v-if="!isLoading">
                        <div class="chat-layout" :class="{ 'has-selected-chat': activePrivateChat || activeGroupChat }">
                          <div class="chat-list-container" :class="{ collapsed: activePrivateChat || activeGroupChat }">
                            <div class="mobile-back-button" 
                                 v-if="isMobile && (activePrivateChat || activeGroupChat)"
                                 @click="activePrivateChat = null; activeGroupChat = null">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" fill="#484444"/>
                              </svg>
                            </div>
                            <div class="chat-list">
                              <div v-for="chat in (privateChats || [])" 
                                   :key="chat.id" 
                                   class="chat-item" 
                                   :class="{ active: activePrivateChat === chat.id }"
                                   :style="getPrivateChatStyle(chat.id)"
                                   @click="selectPrivateChat(chat.id)">
                                <div class="avatar">
                                  <div v-if="chat.avatar" 
                                       class="chat-avatar" 
                                       :style="{ backgroundImage: 'url(' + chat.avatar + ')' }"></div>
                                  <div v-else class="chat-avatar default-private"></div>
                                </div>
                                <div class="chat-info">
                                  <div class="name">{{ chat.name }}</div>
                                  <div class="last-message">{{ stripBbCode(chat.lastMessage) }}</div>
                                </div>
                                <div class="chat-meta">
                                  <div class="time">{{ chat.time }}</div>
                                  <div class="unread" v-if="chat.unread > 0">{{ chat.unread }}</div>
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          <div class="empty-state" v-if="showEmptyState">
                            <p>Нет личных сообщений</p>
                          </div>
                          
                          <div class="message-area"  v-if="activePrivateChat || activeGroupChat">
                            <div class="message-container">
                              <div class="message-header" v-if="getPrivateChat(activePrivateChat)?.name">
                                <h6>{{ getPrivateChat(activePrivateChat)?.name }}</h6>
                              </div>
                              <div class="messages" ref="privateMessages" v-if="activeTab === 'private'">
                                <div v-if="activePrivateChat && privateMessages.length === 0" class="welcome-message">
                                  <div class="message-content">Введите ваш вопрос ниже ...</div>
                                </div>
                                <!-- Индикатор новых сообщений -->
                                <div v-if="newMessagesNotifications.private.visible && activePrivateChat === newMessagesNotifications.private.chatId" 
                                     class="new-messages-indicator" @click="scrollToBottomWithNotification('private')">
                                    <div class="indicator-content">
                                        <span>{{ newMessagesNotifications.private.count }} нов{{ newMessagesNotifications.private.count === 1 ? 'ое' : 'ых' }} сообщени{{ newMessagesNotifications.private.count === 1 ? 'е' : 'я' }}</span>
                                        <button class="scroll-btn">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M16 13l-4 4-4-4M12 6v11"/>
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                                <div v-for="message in (privateMessages || [])" 
                                     :key="message.id" 
                                     class="message-wrapper"
                                     :class="{ 
                                     system: message.isSystem,
                                     read: message.read || message.outgoing,
                                     unread: !message.read && !message.outgoing 
                                     }"
                                     :data-message-id="message.id"
                                 >
                                     
                                  <div v-if="!message.isSystem" class="message" :class="{ outgoing: message.outgoing }">
                                    <div class="message-sender">
                                      <div class="sender-avatar" v-if="message.avatar" 
                                           :style="{ backgroundImage: 'url(' + message.avatar + ')' }"></div>
                                      <div class="sender-avatar default-avatar" v-else></div>
                                      <div class="sender-name">{{ message.name || '' }}</div>
                                    </div>
                                    <div class="message-content" 
                                         :class="{ truncated: shouldTruncate(message.text) && !isMessageExpanded(message.id) }"
                                         v-html="formatBbCode(message.text, message.id)">
                                    </div>
                                    <div v-if="shouldTruncate(message.text)" class="message-expand">
                                      <button class="expand-btn" @click="toggleMessageExpand(message.id)">
                                        {{ isMessageExpanded(message.id) ? 'Свернуть' : 'Развернуть' }}
                                      </button>
                                    </div>
                                    <div class="message-time">{{ message.time }}</div>
                                  </div>
                                  
                                  <div v-else class="message system-message">
                                    <div class="message-content" 
                                         :class="{ truncated: shouldTruncate(message.text) && !isMessageExpanded(message.id) }"
                                         v-html="formatBbCode(message.text, message.id)">
                                    </div>
                                    <div v-if="shouldTruncate(message.text)" class="message-expand">
                                      <button class="expand-btn" @click="toggleMessageExpand(message.id)">
                                        {{ isMessageExpanded(message.id) ? 'Свернуть' : 'Развернуть' }}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div class="message-input">
                                <input type="text" 
                                       v-model="privateMessageText" 
                                       @keyup.enter="sendPrivateMessage" 
                                       placeholder="Написать сообщение...">
                                <button @click="sendPrivateMessage">Отправить</button>
                              </div>
                            </div>
                          </div>
                          
                          <div class="empty-state select-chat" v-if="privateChats.length > 0 && !activePrivateChat">
                            <p>Выберите чат для общения</p>
                          </div>
                        </div>
                      </div>
                      
                      <div :class="['tab-content', { active: activeTab === 'group' }]" v-if="!isLoading">
                        <div class="chat-layout" :class="{ 'has-selected-chat': activePrivateChat || activeGroupChat }">
                          <div class="chat-list-container" :class="{ collapsed: activePrivateChat || activeGroupChat }">
                            <div class="mobile-back-button" 
                                 v-if="isMobile && (activePrivateChat || activeGroupChat)"
                                 @click="activePrivateChat = null; activeGroupChat = null">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" fill="#484444"/>
                              </svg>
                            </div>
                            <div class="chat-list">
                              <div v-for="chat in (groupChats || [])" 
                                   :key="chat.id" 
                                   class="chat-item" 
                                   :class="{ active: activeGroupChat === chat.id }"
                                   :style="getGroupChatStyle(chat.id)"
                                   @click="selectGroupChat(chat.id)">
                                <div class="avatar">
                                  <div v-if="chat.avatar" 
                                       class="chat-avatar" 
                                       :style="{ backgroundImage: 'url(' + chat.avatar + ')' }"></div>
                                  <div v-else class="chat-avatar default-group"></div>
                                </div>
                                <div class="chat-info">
                                  <div class="name">{{ chat.name }}</div>
                                  <div class="last-message">{{ stripBbCode(chat.lastMessage) }}</div>
                                </div>
                                <div class="chat-meta">
                                  <div class="time">{{ chat.time }}</div>
                                  <div class="unread" v-if="chat.unread > 0">{{ chat.unread }}</div>
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          <div class="empty-state" v-if="groupChats.length === 0">
                            <p>Нет групповых чатов</p>
                          </div>
                          
                          <div class="message-area" v-if="activeGroupChat">
                            <div class="message-container" :style="getGroupChatStyle(activeGroupChat)">
                              <div class="message-header" v-if="getGroupChat(activeGroupChat)?.name">
                                <div class="group-header-info">
                                    <h6>{{ getGroupChat(activeGroupChat).name || 'Групповой чат' }}</h6>
                                    <a v-if="getGroupChat(activeGroupChat).groupLink" 
                                       :href="getGroupChat(activeGroupChat).groupLink" 
                                       :style="getGroupLinkStyle(activeGroupChat)"
                                       class="group-link" 
                                       target="_blank"
                                       @click.stop>
                                      <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style="margin-right: 4px;">
                                        <path d="M18 10.82a1 1 0 0 0-1 1V19a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1h7.18a1 1 0 0 0 0-2H5a3 3 0 0 0-3 3v11a3 3 0 0 0 3 3h11a3 3 0 0 0 3-3v-7.18a1 1 0 0 0-1-1Z"/>
                                        <path d="M19.92 2.62a1 1 0 0 0-.54-.54A1 1 0 0 0 19 2h-6a1 1 0 0 0 0 2h3.59l-9.3 9.29a1 1 0 0 0 0 1.42 1 1 0 0 0 1.42 0L18 5.41V9a1 1 0 0 0 2 0V3a1 1 0 0 0-.08-.38Z"/>
                                      </svg>
                                      Перейти к группе
                                    </a>
                              </div>
                              </div>
                              <div class="messages" ref="groupMessages"  v-if="activeTab === 'group'">
                                <!-- Индикатор новых сообщений -->
                                <div v-if="newMessagesNotifications.group.visible && activeGroupChat === newMessagesNotifications.group.chatId" 
                                     class="new-messages-indicator" @click="scrollToBottomWithNotification('group')">
                                    <div class="indicator-content">
                                        <span>{{ newMessagesNotifications.group.count }} нов{{ newMessagesNotifications.group.count === 1 ? 'ое' : 'ых' }} сообщени{{ newMessagesNotifications.group.count === 1 ? 'е' : 'я' }}</span>
                                        <button class="scroll-btn">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M16 13l-4 4-4-4M12 6v11"/>
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                                <div v-for="message in (groupMessages || [])" 
                                     :key="message.id" 
                                     class="message-wrapper"
                                     :class="{ 
                                         system: message.isSystem,
                                         read: !message.unread || message.outgoing,
                                         unread: message.unread && !message.outgoing 
                                     }"
                                     :data-message-id="message.id"
                                     >
                                     
                                    <div v-if="!message.isSystem" class="message" :class="{ outgoing: message.outgoing }">
                                        <!-- Отображение превью ответа -->
                                        <div v-if="message.parentId || message.parentText" class="reply-preview">
                                            <div class="reply-header">
                                                <span class="reply-author">{{ message.parentAuthor || 'Ответ на' }}</span>
                                            </div>
                                            <div class="reply-text">{{ stripBbCode(message.parentText || '').substring(0, 50) }}{{ message.parentText && message.parentText.length > 50 ? '...' : '' }}</div>
                                        </div>
                                        
                                        <div class="message-sender" v-if="!message.outgoing">
                                            <div class="sender-avatar" v-if="message.avatar" 
                                                 :style="{ backgroundImage: 'url(' + message.avatar + ')' }"></div>
                                            <div class="sender-avatar default-avatar" v-else></div>
                                            <div class="sender-name">{{ message.name || '' }}</div>
                                        </div>
                                        
                                        <div class="message-content" 
                                             :class="{ truncated: shouldTruncate(message.text) && !isMessageExpanded(message.id) }"
                                             v-html="formatBbCode(message.text, message.id)">
                                        </div>
                                        <div v-if="shouldTruncate(message.text)" class="message-expand">
                                          <button class="expand-btn" @click="toggleMessageExpand(message.id)">
                                            {{ isMessageExpanded(message.id) ? 'Свернуть' : 'Развернуть' }}
                                          </button>
                                        </div>
                                        <div class="message-time">{{ message.time }}</div>
                                        
                                        <!-- Кнопка ответа -->
                                        <div class="message-actions" v-if="!message.outgoing && !message.isSystem">
                                            <button class="reply-btn" @click="activateReply(message.id)" title="Ответить">
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                                    <path d="M10 9V5l-7 7 7 7v-4.1c5 0 8.5 1.6 11 5.1-1-5-4-10-11-11z"/>
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                    
                                    <div v-else class="message system-message">
                                        <div class="message-content" 
                                             :class="{ truncated: shouldTruncate(message.text) && !isMessageExpanded(message.id) }"
                                             v-html="formatBbCode(message.text, message.id)">
                                        </div>
                                        <div v-if="shouldTruncate(message.text)" class="message-expand">
                                          <button class="expand-btn" @click="toggleMessageExpand(message.id)">
                                            {{ isMessageExpanded(message.id) ? 'Свернуть' : 'Развернуть' }}
                                          </button>
                                        </div>
                                    </div>
                                </div>
                              </div>
                              <div class="message-input" v-if="activeTab === 'group'">
                                <div class="reply-indicator" v-if="replyToMessageId && groupReplyText">
                                    <div class="reply-indicator-content">
                                        <span>{{ groupReplyText }}</span>
                                        <button class="cancel-reply" @click="cancelReply">×</button>
                                    </div>
                                </div>
                                <input type="text" 
                                       ref="groupInput"
                                       v-model="groupMessageText" 
                                       @keyup.enter="sendGroupMessage" 
                                       placeholder="Написать сообщение...">
                                <button @click="sendGroupMessage">Отправить</button>
                                </div>
                            </div>
                          </div>
                          
                          <div class="empty-state select-chat" v-if="groupChats.length > 0 && !activeGroupChat">
                            <p>Выберите групповой чат</p>
                          </div>
                        </div>
                      </div>
                      
                      <div :class="['tab-content', { active: activeTab === 'notifications' }]" v-if="!isLoading">
                        <div class="notifications-list" v-if="notifications.length > 0">
                          <div class="notifications-header">
                            <button
                              class="btn btn-link"
                              @click="markAllNotificationsAsRead" 
                              v-if="!isLoading && hasUnreadNotifications">
                              Прочитать все
                            </button>
                          </div>
                          <div v-for="notification in (notifications || [])" 
                               :key="notification.id" 
                               class="notification-item" 
                               :class="{ unread: !notification.read && notification.unread !== 0 }">
                            <div class="notification-icon-wrapper">
                              <i :class="getNotificationIcon(notification.type)"></i>
                            </div>
                            <div class="notification-content">
                              <div class="notification-text" v-html="formatBbCode(notification.text)"></div>
                              <div class="notification-time">{{ notification.time }}</div>
                            </div>
                            <div class="notification-actions">
                              <button v-if="!notification.read" @click="markAsRead(notification.id)">Прочитано</button>
                            </div>
                          </div>
                        </div>
                        <div class="empty-state" v-if="notifications.length === 0">
                          <p>Нет уведомлений</p>
                        </div>
                      </div>
                      
                    </div>
                    <div class="chat-content" v-else>
                        <!-- Показываем верификацию для неавторизованных пользователей -->
                        <div v-if="showVerification && !isVerificationComplete" class="verification-fullscreen">
                            <div class="verification-wrapper">
                                <VerificationManager
                                    :is-auth="false"
                                    :show-name-field="true"
                                    :show-last-name-field="true"
                                    :show-question-field="false"
                                    :compact-mode="true"
                                    @verification-complete="onVerificationComplete"
                                />
                            </div>
                        </div>
                        
                        <!-- Показываем успешную верификацию -->
                        <div v-if="isVerificationComplete" class="verification-success">
                            <div class="success-content">
                                <div class="success-icon">✓</div>
                                <h6>Добро пожаловать в чат!</h6>
                                <p>Теперь вы можете общаться в личных и групповых чатах</p>
                                
                                <div class="success-actions">
                                    <button @click="logoutFromVerification" class="button-outline">
                                        Выйти
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                  </div>
                </div>
              `,
        })

        api.setApplication(app)
        app.component('Loader', Loader)
        app.mount(this.rootNode)
    }

    resetScrollBehavior(chatId, type) {
        const key = `${type}_${chatId}`;
        if (this.userScrollBehavior) {
            this.userScrollBehavior.delete(key);
        }
    }

    safeScrollToBottom(ref) {
        this.scrollToBottom(ref);
    }

    resetInitialLoad(chatId, type) {
        const key = `${type}_${chatId}`;
        this.#isInitialLoad.delete(key);
    }

    destroy() {
        this.stopListening()
        this.#stopActiveChatUpdates()

        if (this.#application) {
            this.#application.unmount()
            this.#application = null
        }

        if (window.ChatManager) {
            window.ChatManager.unregister(this);
        }

        this.#apiCache.clear();
        this.#pendingRequests.clear();
    }

    async refreshGroupChat(groupId) {
        if (!this.#application) return;

        const app = this.#application._instance.proxy;

        if (app.activeGroupChat === groupId) {
            await this.#loadMessagesForChat(groupId, 'group');
        }

        await this.checkForUpdates();
    }

    async openGroupChatOnPage(chatId, containerSelector = '#specific-group-chat') {
        if (!this.#isAuthenticated()) {
            console.error('Пользователь не авторизован');
            this.#showError('Для доступа к чату необходимо авторизоваться');
            return;
        }

        if (!chatId) {
            console.error('Chat ID is required');
            this.#showError('Не указан ID чата');
            return;
        }

        const container = document.querySelector(containerSelector);
        if (!container) {
            console.error('Container not found:', containerSelector);
            this.#showError('Контейнер для чата не найден');
            return;
        }

        container.innerHTML = '';

        const chatHTML = `
        <div class="group-chat-page">
            <div class="messages-container">
                <div class="messages-list" id="group-messages-${chatId}">
                    <div class="messages-loading">
                        <div class="loading-spinner"></div>
                        <span>Загрузка сообщений...</span>
                    </div>
                </div>
            </div>
            
            <div class="message-input-wrapper">
                <div class="input-group">
                    <div class="textarea-wrapper">
                        <textarea class="message-textarea" placeholder="Введите сообщение..." rows="2"></textarea>
                        <span class="textarea-counter">0/1000</span>
                    </div>
                    <button class="send-button">
                        <svg class="send-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                        </svg>
                        <span class="send-text">Отправить</span>
                    </button>
                </div>
            </div>
        </div>
    `;

        container.innerHTML = chatHTML;

        this.#setupGroupChatHandlers(chatId);
        this.#loadGroupChatMessages(chatId);
    }

    async #loadGroupChatMessages(chatId) {
        try {
            const container = document.getElementById(`group-messages-${chatId}`);
            if (!container) return;

            const tempMessages = container.querySelectorAll('.message-temp');
            const loadingHTML = `
            <div class="messages-loading">
                <div class="loading-spinner"></div>
                <span>Загрузка сообщений...</span>
            </div>
        `;

            const tempMessagesHTML = Array.from(tempMessages).map(msg => msg.outerHTML).join('');
            container.innerHTML = loadingHTML + tempMessagesHTML;

            const response = await this.callBitrixAPI(API_METHODS.DIALOG_MESSAGES_GET, {
                CHAT_ID: chatId
            });

            if (response.status === 'success') {
                this.#renderGroupMessages(chatId, response.data || []);
            } else {
                container.innerHTML = `
                <div class="error-state">
                    <p>Ошибка загрузка сообщений</p>
                </div>
            ` + tempMessagesHTML;
            }
        } catch (error) {
            console.error('Error loading group messages:', error);
            const container = document.getElementById(`group-messages-${chatId}`);
            if (container) {
                const tempMessages = container.querySelectorAll('.message-temp');
                const tempMessagesHTML = Array.from(tempMessages).map(msg => msg.outerHTML).join('');
                container.innerHTML = `
                <div class="error-state">
                    <p>Ошибка загрузки сообщений</p>
                </div>
            ` + tempMessagesHTML;
            }
        }
    }

    #renderGroupMessages(chatId, messages) {
        const container = document.getElementById(`group-messages-${chatId}`);
        if (!container) return;

        const userId = this.#options.userId || BX.message('USER_ID');

        if (messages.length === 0) {
            container.innerHTML = `
            <div class="no-messages">
                <p>Сообщений пока нет</p>
            </div>
        `;
            return;
        }

        const messagesHTML = messages.map(msg => {
            const isOutgoing = msg.senderId == userId;
            const isSystem = msg.system === 1;

            if (isSystem) {
                return `
                <div class="message-item message-system">
                    <div class="system-message">
                        <div class="message-content">${this.#formatBbCode(msg.text || '')}</div>
                    </div>
                </div>
            `;
            } else {
                return `
                <div class="message-item ${isOutgoing ? 'message-outgoing' : 'message-incoming'}" data-message-id="${msg.id}">
                    <div class="user-message">
                        ${msg.name ? `
                            <div class="message-header">
                                <span class="sender-name">${msg.name}</span>
                            </div>
                        ` : ''}
                        <div class="message-content">${this.#formatBbCode(msg.text || '')}</div>
                        ${msg.parentMessageId ? `
                            <div class="reply-preview">
                                <div class="reply-author">${msg.parentMessageAuthor || 'Ответ на'}:</div>
                                <div class="reply-text">${this.#formatBbCode(msg.parentMessageText || '')}</div>
                            </div>
                        ` : ''}
                        <div class="message-footer">
                            <span class="message-time">${this.#formatTime(msg.date)}</span>
                            ${!isOutgoing ? `
                                <button class="reply-btn" title="Ответить на это сообщение">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M10 9V5l-7 7 7 7v-4.1c5 0 8.5 1.6 11 5.1-1-5-4-10-11-11z"/>
                                    </svg>
                                    <span class="reply-text">Ответить</span>
                                </button>
                            ` : ''}
                        </div>
                    </div>
                </div>
            `;
            }
        }).join('');

        container.innerHTML = messagesHTML;
        this.#setupReplyHandlers(chatId);
        container.scrollTop = container.scrollHeight;
    }

    #shouldMarkAsRead(messageElement) {
        if (!messageElement || !this.#isChatOpen()) return false;

        const rect = messageElement.getBoundingClientRect();
        return (
            rect.top >= 0 &&
            rect.bottom <= (window.innerHeight || document.documentElement.clientHeight)
        );
    }


    #setupReplyHandlers() {
        if (!this.#application) return;

        const app = this.#application._instance.proxy;
        const containers = [app.$refs.groupMessages].filter(Boolean);

        containers.forEach(container => {
            if (container && !this.replyHandlers.has(container)) {
                const handler = (e) => {
                    const replyBtn = e.target.closest('.reply-btn');
                    if (replyBtn) {
                        const messageId = replyBtn.dataset.messageId;
                        this.#activateReplyMode(messageId);
                    }
                };

                container.addEventListener('click', handler);
                this.replyHandlers.set(container, handler);
            }
        });
    }

    #activateReplyMode(messageId) {
        if (!this.#application) return;

        const app = this.#application._instance.proxy;
        const container = app.$refs.groupMessages;
        if (!container) return;

        const messageElement = container.querySelector(`[data-message-id="${messageId}"]`);
        if (!messageElement) return;

        const messageText = messageElement.querySelector('.message-content')?.textContent || '';
        const authorName = messageElement.querySelector('.sender-name')?.textContent || 'Сообщение';

        // Показываем индикатор ответа
        app.replyToMessageId = messageId;
        app.groupReplyText = `Ответ для ${authorName}: ${this.#stripBbCode(messageText).substring(0, 50)}...`;

        // Фокусируемся на поле ввода
        setTimeout(() => {
            const input = app.$refs.groupInput;
            if (input) {
                input.focus();
            }
        }, 100);
    }

    #stripBbCode(text) {
        if (!text) return '';

        return text
            .replace(/\[b\](.*?)\[\/b\]/g, '$1')
            .replace(/\[i\](.*?)\[\/i\]/g, '$1')
            .replace(/\[u\](.*?)\[\/u\]/g, '$1')
            .replace(/\[s\](.*?)\[\/s\]/g, '$1')
            .replace(/\[br\]/g, ' ')
            .replace(/\[url=(.*?)\](.*?)\[\/url\]/g, '$2')
            .replace(/\[url\](.*?)\[\/url\]/g, '$1')
            .replace(/\[color=(.*?)\](.*?)\[\/color\]/g, '$2')
            .replace(/\[size=(.*?)\](.*?)\[\/size\]/g, '$2')
            .replace(/\[quote\](.*?)\[\/quote\]/gs, '$1')
            .replace(/\[code\](.*?)\[\/code\]/gs, '$1')
            .replace(/\[img\](.*?)\[\/img\]/g, '📷')
            .replace(/\[list\](.*?)\[\/list\]/gs, '$1')
            .replace(/\[\*\](.*?)(\n|$)/g, '$1')
            .replace(/\n/g, ' ');
    }

    #setupGroupChatHandlers(chatId) {
        const container = document.querySelector('#specific-group-chat');
        if (!container) return;

        const textarea = container.querySelector('.message-textarea');
        const sendButton = container.querySelector('.send-button');
        const counter = container.querySelector('.textarea-counter');
        const messagesContainer = container.querySelector('.messages-list');

        if (textarea && counter) {
            textarea.addEventListener('input', () => {
                const length = textarea.value.length;
                counter.textContent = `${length}/1000`;

                if (length > 1000) {
                    counter.classList.add('limit-reached');
                } else {
                    counter.classList.remove('limit-reached');
                }
            });
        }

        if (textarea && sendButton && messagesContainer) {
            const sendMessage = async () => {
                const message = textarea.value.trim();
                if (!message) return;

                const tempMessageId = 'temp-' + Date.now();
                const messageText = message;
                const parentMessageData = this.#currentReply;

                this.#addTempMessage(messagesContainer, {
                    id: tempMessageId,
                    text: messageText,
                    senderId: this.#options.userId || BX.message('USER_ID'),
                    name: this.#getCurrentUserName(),
                    date: new Date().toISOString(),
                    parentMessageId: parentMessageData?.parentMessageId,
                    parentMessageText: parentMessageData?.messageText,
                    parentMessageAuthor: parentMessageData?.senderName,
                    isTemp: true
                });

                sendButton.disabled = true;
                sendButton.classList.add('send-button-loading');
                textarea.disabled = true;

                const messageData = {
                    CHAT_ID: chatId,
                    MESSAGE: message
                };

                if (parentMessageData && parentMessageData.parentMessageId) {
                    messageData.PARENT_MESSAGE_ID = parentMessageData.parentMessageId;
                }

                try {
                    const response = await this.#sendGroupMessageInPage(chatId, message, parentMessageData?.parentMessageId);

                    if (response.status === 'success' && response.data?.messageId) {
                        this.#replaceTempMessage(messagesContainer, tempMessageId, response.data.messageId);

                        textarea.value = '';
                        if (counter) {
                            counter.textContent = '0/1000';
                            counter.classList.remove('limit-reached');
                        }
                        this.cancelReply()

                        await this.refreshAllChats();
                        if (window.ChatManager) {
                            setTimeout(() => {
                                window.ChatManager.refreshAll().catch(error => {
                                    console.error('Error refreshing chats:', error);
                                });
                            }, 500);
                        }

                        const refreshEvent = new CustomEvent('chatNeedsRefresh');
                        document.dispatchEvent(refreshEvent);

                        this.triggerChatRefresh();

                    } else {
                        this.#removeTempMessage(messagesContainer, tempMessageId);
                        this.#showError('Ошибка отправки сообщения');
                    }
                } catch (error) {
                    console.error('Error sending message:', error);
                    this.#removeTempMessage(messagesContainer, tempMessageId);
                    this.#showError('Ошибка отправки сообщения');
                } finally {
                    sendButton.disabled = false;
                    sendButton.classList.remove('send-button-loading');
                    textarea.disabled = false;
                    textarea.focus();
                }
            };

            sendButton.addEventListener('click', sendMessage);

            textarea.addEventListener('keypress', (e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                }
            });

            textarea.addEventListener('keydown', (e) => {
                if (e.key === 'Escape' && this.#currentReply) {
                    this.cancelReply()
                    e.preventDefault();
                }
            });

            const refreshButton = container.querySelector('.btn');
            if (refreshButton) {
                refreshButton.addEventListener('click', async () => {
                    refreshButton.disabled = true;
                    refreshButton.textContent = 'Обновление...';

                    try {
                        await this.refreshAllChats();
                    } catch (error) {
                        console.error('Error refreshing chats:', error);
                    } finally {
                        refreshButton.disabled = false;
                        refreshButton.textContent = 'Обновить';
                    }
                });
            }
        }
    }

    triggerChatRefresh() {
        const event = new CustomEvent('chatNeedsRefresh', {
            detail: {timestamp: Date.now()}
        });
        document.dispatchEvent(event);

        localStorage.setItem('chatLastUpdate', Date.now());

        if (window.ChatManager) {
            window.ChatManager.refreshAll();
        }
    }

    #addTempMessage(container, messageData) {
        const userName = this.#getCurrentUserName();

        const messageHTML = `
        <div class="message-item message-outgoing message-temp" data-message-id="${messageData.id}">
            <div class="user-message">
                <div class="message-header">
                    <span class="sender-name">${userName}</span>
                </div>
                <div class="message-content">${this.#formatBbCode(messageData.text || '')}</div>
                ${messageData.parentMessageId ? `
                    <div class="reply-preview">
                        <div class="reply-author">Ответ для ${messageData.parentMessageAuthor}:</div>
                        <div class="reply-text">${this.#formatBbCode(messageData.parentMessageText || '').substring(0, 100)}${messageData.parentMessageText.length > 100 ? '...' : ''}</div>
                    </div>
                ` : ''}
                <div class="message-footer">
                    <span class="message-time">Отправка...</span>
                </div>
            </div>
        </div>
    `;

        container.insertAdjacentHTML('beforeend', messageHTML);
        container.scrollTop = container.scrollHeight;
    }

    #replaceTempMessage(container, tempMessageId, realMessageId) {
        const tempMessage = container.querySelector(`[data-message-id="${tempMessageId}"]`);
        if (tempMessage) {
            tempMessage.dataset.messageId = realMessageId;
            tempMessage.classList.remove('message-temp');

            const timeElement = tempMessage.querySelector('.message-time');
            if (timeElement) {
                timeElement.textContent = this.#formatTime(new Date());
            }
        }
    }

    #removeTempMessage(container, tempMessageId) {
        const tempMessage = container.querySelector(`[data-message-id="${tempMessageId}"]`);
        if (tempMessage) {
            tempMessage.remove();
        }
    }

    #formatBbCode(text, messageId = null) {
        if (!text) return '';

        return text
            .replace(/\[b\](.*?)\[\/b\]/g, '<strong>$1</strong>')
            .replace(/\[i\](.*?)\[\/i\]/g, '<em>$1</em>')
            .replace(/\[u\](.*?)\[\/u\]/g, '<u>$1</u>')
            .replace(/\[s\](.*?)\[\/s\]/g, '<s>$1</s>')
            .replace(/\[br\]/g, '<br>')
            .replace(/\[url=(.*?)\](.*?)\[\/url\]/g, '<a href="$1" target="_blank">$2</a>')
            .replace(/\[url\](.*?)\[\/url\]/g, '<a href="$1" target="_blank">$1</a>')
            .replace(/\n/g, '<br>');
    }

    #formatTime(dateString) {
        if (!dateString) return '';

        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';

        return date.toLocaleTimeString('ru-RU', {
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    #showError(message) {
        if (typeof BX.UI.Notification !== 'undefined') {
            BX.UI.Notification.Center.notify({
                content: message,
                autoHideDelay: 3000
            });
        } else {
            console.error('Chat error:', message);
        }
    }

    #getCurrentUserName() {
        if (typeof BX !== 'undefined' && BX.message && BX.message('USER_NAME')) {
            return BX.message('USER_NAME');
        }

        if (this.#options.userName) {
            return this.#options.userName;
        }

        return 'Вы';
    }

    async refreshAllChats() {
        const activeChat = this.#currentReply?.chatId || this.#getActiveChatId();
        if (activeChat) {
            await this.#loadGroupChatMessages(activeChat);
        }

        if (this.#application) {
            try {
                await this.checkForUpdates();
            } catch (error) {
                console.error('Error refreshing main chat:', error);
            }
        }
    }

    #getActiveChatId() {
        const container = document.querySelector('#specific-group-chat');
        if (!container) return null;

        const messageItems = container.querySelectorAll('.message-item');
        if (messageItems.length > 0) {
            for (let item of messageItems) {
                if (item.dataset.messageId && !item.dataset.messageId.startsWith('temp-')) {
                    break;
                }
            }
        }
        return null;
    }

    async refresh() {
        await this.refreshAllChats();
    }

    #getGroupIdFromContainer(container) {
        // Пытаемся найти ID группы в контейнере или его родителях
        let element = container;
        while (element && element !== document.body) {
            if (element.dataset.groupId) {
                return element.dataset.groupId;
            }
            element = element.parentElement;
        }
        return null;
    }

    #updateGroupChatUI(container, message) {
        const messagesContainer = container.querySelector('.messages-list');
        if (!messagesContainer) return;

        // Проверяем, нет ли уже такого сообщения
        const existingMessage = messagesContainer.querySelector(`[data-message-id="${message.id}"]`);
        if (existingMessage) return;

        const messageHTML = this.#createGroupMessageHTML(message);
        messagesContainer.insertAdjacentHTML('beforeend', messageHTML);

        // Прокручиваем вниз
        setTimeout(() => {
            messagesContainer.scrollTop = messagesContainer.scrollHeight;
        }, 100);
    }

    #createGroupMessageHTML(message) {
        const userId = this.#options.userId || BX.message('USER_ID') || 0;
        const isOutgoing = parseInt(message.senderId) === parseInt(userId);
        const isSystem = message.system === 1;
        const hasParent = message.parentId && message.parentId > 0;

        if (isSystem) {
            return `
            <div class="message-item message-system" data-message-id="${message.id}">
                <div class="system-message">
                    <div class="message-content">${this.#formatBbCode(message.text || '')}</div>
                    <div class="message-time">${this.#formatTime(message.date)}</div>
                </div>
            </div>
        `;
        }

        return `
        <div class="message-item ${isOutgoing ? 'message-outgoing' : 'message-incoming'}" 
             data-message-id="${message.id}">
            <div class="user-message">
                ${message.name ? `
                    <div class="message-header">
                        <span class="sender-name">${message.name}</span>
                    </div>
                ` : ''}
                
                ${hasParent ? this.#createReplyPreview(message) : ''}
                
                <div class="message-content">${this.#formatBbCode(message.text || '')}</div>
                <div class="message-footer">
                    <span class="message-time">${this.#formatTime(message.date)}</span>
                    ${!isOutgoing ? `
                        <button class="reply-btn" data-message-id="${message.id}">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M10 9V5l-7 7 7 7v-4.1c5 0 8.5 1.6 11 5.1-1-5-4-10-11-11z"/>
                            </svg>
                        </button>
                    ` : ''}
                </div>
            </div>
        </div>
    `;
    }

    #formatBbCodeForGroup(text) {
        if (!text) return '';
        return text
            .replace(/\[b\](.*?)\[\/b\]/g, '<strong>$1</strong>')
            .replace(/\[i\](.*?)\[\/i\]/g, '<em>$1</em>')
            .replace(/\[u\](.*?)\[\/u\]/g, '<u>$1</u>')
            .replace(/\[s\](.*?)\[\/s\]/g, '<s>$1</s>')
            .replace(/\[br\]/g, '<br>')
            .replace(/\[url=(.*?)\](.*?)\[\/url\]/g, '<a href="$1" target="_blank">$2</a>')
            .replace(/\[url\](.*?)\[\/url\]/g, '<a href="$1" target="_blank">$1</a>')
            .replace(/\n/g, '<br>');
    }

    #formatTimeForGroup(dateString) {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        return date.toLocaleTimeString('ru-RU', {
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    #synchronizeWithGroupChats(messageData) {
        if (messageData.type !== 'group') return;

        // Ищем все групповые чаты на странице
        const groupChatContainers = document.querySelectorAll('#specific-group-chat');

        groupChatContainers.forEach(container => {
            const groupId = container.dataset.groupId;
            if (groupId && groupId == messageData.chatId) {
                this.#updateGroupChatUI(container, messageData.message);
            }
        });
    }

    #createReplyPreview(message) {
        if (!message.parentId && !message.parentMessageId) return '';

        const parentInfo = message.parent || {};
        const parentText = parentInfo.text || message.parentMessageText || 'сообщение';
        const authorName = parentInfo.author || message.parentMessageAuthor || 'Ответ на';

        return `
        <div class="reply-preview" data-parent-id="${message.parentId || message.parentMessageId}">
            <div class="reply-header">
                <span class="reply-author">${this.#escapeHtml(authorName)}</span>
            </div>
            <div class="reply-text">${this.#stripBbCode(parentText).substring(0, 100)}${parentText.length > 100 ? '...' : ''}</div>
        </div>
    `;
    }

    #escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    #setupScrollHandlers() {
        if (!this.#application) return;

        const app = this.#application._instance.proxy;
        const containers = [
            {ref: 'privateMessages', type: 'private'},
            {ref: 'groupMessages', type: 'group'}
        ];

        containers.forEach(({ref, type}) => {
            const container = app.$refs[ref];
            if (container && !this.scrollHandlers.has(ref)) {
                const handler = () => this.#handleScroll(ref, type);
                container.addEventListener('scroll', handler);
                this.scrollHandlers.set(ref, handler);
            }
        });
    }

    scrollToBottom(ref) {
        if (!this.#application) return;

        const app = this.#application._instance.proxy;
        const container = app.$refs[ref];

        if (container) {
            container.scrollTo({
                top: container.scrollHeight,
                behavior: 'smooth'
            });

            // Сбрасываем счетчик непрочитанных для этого чата
            const [type, chatId] = ref.replace('Messages', '').split('_');
            this.unreadMessages.delete(`${type}_${chatId}`);
        }
    }

    #handleScroll(ref, type) {
        const app = this.#application._instance.proxy;
        const container = app.$refs[ref];

        if (container && this.#isAtBottom(container)) {
            // Если прокрутили до конца - очищаем уведомления
            const chatId = type === 'private' ? app.activePrivateChat : app.activeGroupChat;
            this.unreadMessages.delete(`${type}_${chatId}`);

            const notification = container.querySelector('.new-messages-notification');
            if (notification) {
                notification.remove();
            }
        }
    }

    activateReply(messageId) {
        if (!this.#application) return;

        const app = this.#application._instance.proxy;
        const container = app.$refs.groupMessages;
        if (!container) return;

        const messageElement = container.querySelector(`[data-message-id="${messageId}"]`);
        if (!messageElement) return;

        const messageText = messageElement.querySelector('.message-content')?.textContent || '';
        const authorName = messageElement.querySelector('.sender-name')?.textContent || 'Сообщение';

        app.replyToMessageId = messageId;
        app.groupReplyText = `Ответ для ${authorName}: ${messageText.substring(0, 50)}${messageText.length > 50 ? '...' : ''}`;

        setTimeout(() => {
            const input = app.$refs.groupInput;
            if (input) {
                input.focus();
            }
        }, 100);
    }

    cancelReply() {
        if (!this.#application) return;

        const app = this.#application._instance.proxy;
        app.replyToMessageId = null;
        app.groupReplyText = '';
    }

    getVueInstance() {
        return this.#application ? this.#application._instance.proxy : null;
    }

    async loadMessagesForChat(chatId, type) {
        return this.#loadMessagesForChat(chatId, type);
    }
}

// Автоматическая синхронизация при изменении сообщений
setInterval(() => {
    if (window.ChatManager) {
        window.ChatManager.refreshAll();
    }
}, 30000); // Каждые 30 секунд

window.getChatAPI = function () {
    return {
        callBitrixAPI: function (method, data) {
            if (window.DzenChat && typeof window.DzenChat.callBitrixAPI === 'function') {
                return window.DzenChat.callBitrixAPI(method, data);
            }
            return Promise.resolve({status: 'error', message: 'Chat not available'});
        }
    };
};